import { DeliveryCompanyField } from './DeliveryCompanyField';
import { openingParcelOf } from './openingParcel';
import { OutboundCarrierGateError, outboundCarrierBlock } from '@/utils/fulfillmentOutboundGate';
import { PendingParcelNotice } from '@/pages/fulfillment/components/PendingParcelNotice';
import { useQuery } from '@tanstack/react-query';
import { GigWorkerClient } from '@/infrastructure/GigWorkerClient';
import { GigOrderDetailAction } from '@/pages/oms/orders/[id]/GigOrderDetailAction';
import GigOrderDetailModal from '@/pages/oms/gig-workers/GigOrderDetailModal';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { CheckCell } from '@/pages/fulfillment/components/CheckCell';
import { isCancelledOrder } from '@/pages/fulfillment/viewOrders/runInbound';
import { CancelledGoodsNote } from '@/pages/fulfillment/components/CancelledGoodsNote';
import { isAxiosError } from 'axios';
import { Link, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { attemptFingerprint, AttemptKeys } from '@/utils/attemptKeys';
import { Alert, Badge, Container, Dropdown, Form, Spinner } from 'react-bootstrap';
import { CANCEL_LOCKED_CODE, FulfillmentAdminClient } from '../../../../infrastructure/FulfillmentAdminClient';
import { usePromptDialog } from '@/pages/fulfillment/components/usePromptDialog';
import { useReceivedGoodsCancelDialog } from '@/pages/fulfillment/orders/components/useReceivedGoodsCancelDialog';
import type { CancelAnswer } from '@/pages/fulfillment/orders/components/useReceivedGoodsCancelDialog';
import { CancelLockBadge } from '@/pages/fulfillment/components/CancelLockBadge';
import { cancelGoodsLinesOf } from '@/utils/cancelGoods';
import { cancelLockOf, cancelLockSourceLabel } from '@/utils/fulfillmentCancelLock';
import { kstMonthDayMinute } from '@/utils/fulfillmentTime';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import {
  AUTO_DISMISS,
  type FulfillmentToastState,
  useFulfillmentToast,
} from '@/pages/fulfillment/components/useFulfillmentToast';
import { inboundBulkResult, UNCONFIRMED_BULK_RESULT } from '@/pages/fulfillment/components/bulkResult';
import { describeFailure, serverRefusal } from '@/utils/fulfillmentErrors';
import {
  canCloseUnshipped,
  CLOSED_UNSHIPPED_LABEL,
  describeCloseUnshippedRefusal,
  fulfillmentStatusLabel,
  titleChipLabel,
  wireframeStatusClass,
} from '@/utils/fulfillmentOrderStatus';
import { FulfillmentSound } from '@/pages/fulfillment/components/outboundSound';
import { PrintDispatcher, PrintFailureNotice } from '@/pages/fulfillment/components/PrintDispatcher';
import {
  archiveHasNothing,
  packingSheetJob,
  printParcels,
  sheetOutcomesOf,
  type ParcelPrintOutcome,
} from '@/pages/fulfillment/components/parcelPaperwork';
import type { PrintFailure } from '@/models/FulfillmentPrint';
import { CancelInboundLineModal } from '@/pages/fulfillment/orders/components/CancelInboundLineModal';
import {
  UseExistingStockModal,
  type UseExistingStockAsk,
} from '@/pages/fulfillment/orders/components/UseExistingStockModal';
import {
  offersLineToExistingInventory,
  describeLineToExistingInventoryRefusal,
} from '@/utils/fulfillmentLineToExistingInventory';
import { SplitLineModal } from '@/pages/fulfillment/orders/components/SplitLineModal';
import {
  describeLineSplitBlock,
  describeLineSplitRefusal,
  describeLineSplitResult,
  LINE_SPLIT_STALE_REFUSALS,
} from '@/utils/fulfillmentLineSplit';
import {
  SwitchLineToJitModal,
  type SwitchLineToJitAsk,
} from '@/pages/fulfillment/orders/components/SwitchLineToJitModal';
import {
  describeLineToJitBlock,
  describeLineToJitRefusal,
  describeLineToJitResult,
  LINE_TO_JIT_STALE_REFUSALS,
  offersLineToJit,
} from '@/utils/fulfillmentLineToJit';
import { LineSourcingDetailModal } from '@/pages/fulfillment/orders/components/LineSourcingDetailModal';
import { isLineMemo, lineMemos } from '@/pages/fulfillment/viewOrders/commentNote';
import {
  describeSourcingRefusal,
  hasSourcingDetail,
  PLATFORM_MISMATCH_MESSAGE,
  PLATFORM_MISMATCH_RULE,
  SOURCING_PLATFORM_LABELS,
  SOURCING_PLATFORMS,
  sourcingSummaryOf,
} from '@/utils/fulfillmentSourcingDetail';
import { operatorNoteOf, payloadOf, summarizeInboundCancel } from '@/pages/fulfillment/viewOrders/activityLog';
import { CS_RESOLUTION_METHODS, csResolutionMethodLabel, isCsResolutionMethod } from '@/utils/fulfillmentCsResolution';
import { isV1WarehouseLocation } from '@/pages/fulfillment/viewOrders/orderLineView';
import { PREVIOUS_NOT_RECORDED, editLogRows } from './editLog';
import { StockClient } from '../../../../infrastructure/StockClient';
import { OMSRequestError } from '../../../../infrastructure/OMSClient';
import type {
  CancelInboundLineRequest,
  CancelLineRequest,
  ChangeCsStatusResponse,
  CloneOrderProvider,
  CloneOrderResponse,
  FulfillmentEvent,
  FulfillmentLocationQty,
  FulfillmentMenuStatus,
  ManualCsStatus,
  FulfillmentOrderDetail,
  FulfillmentOrderLine,
  LineTrackingCourier,
  OrderComment,
  PicProfile,
  SplitOrderLinePart,
  SplitOrderLineResponse,
  SwitchCarrierOrderRequest,
  SwitchOrderLineToJitResponse,
  SwitchCarrierOrderResponse,
  TrackingInUseWarning,
  UpdateOrderAddressRequest,
  UnrecognizedAutoMatch,
  UpdateLineAgentTrackingRequest,
} from '../../../../infrastructure/FulfillmentAdminClient';
import {
  canCancelInbound,
  canCancelLine,
  canInboundLine,
  describePackingLabelBlock,
  isOutboundReady,
} from '../../../../utils/fulfillmentLineStatus';
import {
  EMPTY_LINE_DRAFT,
  describeLineEditRefusal,
  lineDraftOf,
  lineEditErrorsOf,
  lineEditRefusalsOf,
} from '@/utils/fulfillmentLineEdit';
import type { LineDraft, LineEditErrors, LineEditField } from '@/utils/fulfillmentLineEdit';
import { purchaseOrderUrl } from '@/utils/fulfillmentPurchaseOrder';
import { getAdminApiUrl, AdminApiDomain } from '../../../../config/ApiRouting';
import { getConfig } from '../../../../config';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { Pencil } from 'lucide-react';
import { kstDay, kstTimestamp } from '../../../../utils/fulfillmentTime';
import { WooCommerceOrderStatus } from '../../../../models/ProductTypes';
import { useAuth } from '../../../../providers/AuthProvider';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';
import { ROUTES } from '../../../../routes/routes';
import { CsStatusBadge } from '@/components/common/CsStatusBadge';
import { AddLineItemModal } from '../components/AddLineItemModal';
import { CloneOrderModal, type CloneOrderDraft } from '../components/CloneOrderModal';
import { CreateCarrierOrderModal } from '../components/CreateCarrierOrderModal';
import { SwitchCarrierOrderModal, type SwitchCarrierSource } from '../components/SwitchCarrierOrderModal';
import {
  readCarrierSwitchDraft,
  SWITCHABLE_FULFILLMENT_STATUSES,
  webOrderBeforeLaunch,
} from '../components/carrierSwitch';
import { useFulfillmentV2Launch } from '@/infrastructure/FulfillmentV2Launch';
import { AuditHistoryModal } from '../components/AuditHistoryModal';
import { CarrierTrackingCards } from './CarrierTrackingCards';
import { PackingVideosChip, PackingVideosSection, usePackingVideos } from '../../components/PackingVideos';
import { ShelfField } from '@/pages/fulfillment/components/ShelfField';
import { readShelfCode, SHELF_CODE_RULE } from '@/pages/fulfillment/viewOrders/shelfCode';
import { OrderPdfPreview } from '@/pages/oms/orders/[id]/OrderPdfPreview';
import type { FulfillmentTracking, SourcingPlatform } from '@/infrastructure/FulfillmentAdminClient';
import {
  currentOutboundShipmentOf,
  isPendingParcel,
  type OutboundParcel,
  isDeleoParcel,
  isOutboundParcel,
  deleoLabelMoreNote,
  deleoLabelNumberOf,
  deleoLabelRefusalOf,
  deleoTrackingOf,
  toDeleoLabelOrder,
} from '@/utils/fulfillmentDeleoLabel';
import { linePlatformLabel, lineRouteLabel } from '@/utils/fulfillmentSource';
import { CopyIcon } from '@/components/ui/badge/CopyIcon';
import { LinkedText, shortUrl } from '@/pages/fulfillment/components/LinkedText';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import { CatalogRequestDialog } from '@/pages/fulfillment/orders/components/CatalogRequestDialog';
import { CogsReportDialog } from '@/pages/fulfillment/orders/components/CogsReportDialog';
import '../../wireframe.css';
import './orderDetail.css';

/**
 * The operations the actor log shows, each with the wireframe's colour for its action word: goods in
 * green, out blue, a cancel red. Listed rather than matched so a later operation that happens to say
 * INBOUND or OUTBOUND does not slip into a log of goods movements.
 */
const ACTOR_LOG_ACTION_CLASSES = new Map<string, string>([
  ['ADMIN_INBOUND', 'act-in'],
  ['ADMIN_INBOUND_CANCEL', 'act-cancel'],
  ['ADMIN_OUTBOUND', 'act-out'],
  ['ADMIN_OUTBOUND_CANCEL', 'act-cancel'],
  ['RETURN_PUTAWAY', 'act-in'],
]);

interface FactField {
  label: string;
  value: string;
  /** Rendered as the OMS status chip rather than plain text, so the two screens read the same. */
  csStatus?: boolean;
  /** Shown beside a resolved CS status, as the list shows it. */
  csResolutionMethod?: string | null;
  /** Renders the value as a link to this in-app route. */
  href?: string;
  /** Renders several in-app links, each with a note after it, in place of the value. */
  links?: FactLink[];
}

interface FactLink {
  label: string;
  href: string;
  note?: string;
}

/** One editable row: the key the backend stores it under and the label OMS puts beside it. */
interface AddressFieldSpec {
  key: string;
  label: string;
  /** The editor's label, where OMS says more in the form than on the card. */
  editLabel?: string;
  placeholder?: string;
  maxLength?: number;
  /** Applied as the operator types, so what is checked and saved is what the carrier will be sent. */
  normalize?: (value: string) => string;
  /** An optional line OMS leaves off the card until it holds something. */
  hiddenWhenEmpty?: boolean;
}

/** Which address the pencil opened. Only one is editable at a time, as on the OMS screen. */
type AddressSlot = 'billingAddress' | 'shippingAddress';

/** One of the two address columns: what it is called, which slot it patches, and what it reads as. */
interface AddressColumn {
  label: string;
  icon: string;
  slot: AddressSlot;
  fields: FactField[];
}

/** The open editor's contents. Consignee and the message ride with shipping, which is what carries them. */
interface AddressDraft {
  address: Record<string, string>;
  consignee: Record<string, string>;
  shippingMessage: string;
}

interface AdminActionErrorBody {
  message?: string | string[];
  violations?: string[];
  /** The rule a sourcing refusal names, such as PLATFORM_MISMATCH. */
  rule?: string;
  /** Set on a stale write the server refused; CONFLICT is the shape every optimistic gate answers with. */
  code?: string;
  /** Who moved the row first, when the server knows; the screen names them rather than saying "someone". */
  changedBy?: string;
  /** Set on a close-unshipped refusal: the lines that already left, or the status the order has moved to. */
  shippedLines?: Array<{ lineNo: number; lineStatus: string }>;
  from?: string | null;
  /** Set on a CANCEL_LOCKED refusal (SSD-7450): when the order was locked against a change-of-mind cancel, and by what. */
  cancelLockedAt?: string | null;
  cancelLockSource?: string | null;
}

const EMPTY_ADDRESS_DRAFT: AddressDraft = { address: {}, consignee: {}, shippingMessage: '' };

/** The rows an address reads and edits as, in the OMS order — one source for both, so they cannot drift. */
const BILLING_ADDRESS_FIELDS: AddressFieldSpec[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'company', label: 'Company', hiddenWhenEmpty: true },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address1', label: 'Address' },
  { key: 'address2', label: 'Address 2', hiddenWhenEmpty: true },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country' },
];

/** Shipping adds the two customs lines the carrier reads; neither exists on a billing address. */
const SHIPPING_ADDRESS_FIELDS: AddressFieldSpec[] = [
  ...BILLING_ADDRESS_FIELDS,
  {
    key: 'taxId',
    label: 'Tax ID',
    editLabel: 'Tax ID (optional, required for certain countries)',
    placeholder: 'e.g. 12.345.678-9',
    maxLength: 30,
  },
  {
    key: 'shortAddressCode',
    label: 'Short Address Code',
    editLabel: 'Saudi Short Address Code (YunExpress, SA)',
    placeholder: 'EGDA6366',
    maxLength: 8,
    normalize: (value) => value.toUpperCase().replace(/\s/g, ''),
  },
];

const addressFieldsFor = (slot: AddressSlot): AddressFieldSpec[] =>
  slot === 'shippingAddress' ? SHIPPING_ADDRESS_FIELDS : BILLING_ADDRESS_FIELDS;

/** Checked here as well as on the server: a bad address saved, then failed at the carrier where nobody looked [T16]. */
const ADDRESS_RULES: Record<string, { pattern: RegExp; message: string }> = {
  country: { pattern: /^[A-Za-z]{2}$/, message: 'Use the two-letter country code, such as KR or JP.' },
  postcode: {
    pattern: /^[A-Za-z0-9][A-Za-z0-9 -]{0,15}$/,
    message: 'A postcode is letters, digits, spaces and hyphens, up to 16 characters.',
  },
  // Saudi National Address short code, as OMS and the server check it (SSD-4697).
  shortAddressCode: {
    pattern: /^[A-Z]{4}[0-9]{4}$/,
    message: 'Must be 4 uppercase letters followed by 4 digits (e.g. EGDA6366).',
  },
};

/** The cap every free-text address field carries on the server; a longer value is refused, not truncated. */
const ADDRESS_TEXT_LIMIT = 255;

/** The rule this field breaks, or nothing; an empty field is not a value and so breaks no rule. */
const describeAddressField = (key: string, value: string): string | null => {
  if (value.trim() === '') {
    return null;
  }
  if (value.length > ADDRESS_TEXT_LIMIT) {
    return `Keep this under ${ADDRESS_TEXT_LIMIT} characters.`;
  }
  const rule = ADDRESS_RULES[key];
  if (!rule) {
    return null;
  }
  return rule.pattern.test(value.trim()) ? null : rule.message;
};

/**
 * The server answers a rejected address with one sentence per broken field, each naming its field.
 * Read back onto the field it names, so the operator fixes the box rather than hunting the toast [T16].
 */
const addressRefusalsOf = (message: string | string[] | undefined, keys: string[]): Record<string, string> => {
  const sentences = Array.isArray(message) ? message : message ? [message] : [];
  const byField: Record<string, string> = {};
  // Nested validation prefixes the property path (`shippingAddress.taxId ...`); only one slot travels per save.
  const unprefixed = sentences.map((sentence) => sentence.replace(/^(shippingAddress|billingAddress)\./, ''));
  for (const key of keys) {
    const named = unprefixed.find((sentence) => sentence.toLowerCase().startsWith(key.toLowerCase()));
    if (named) {
      byField[key] = named;
    }
  }
  return byField;
};

/** The statuses the server refuses an address edit in: the parcel has left, or the order is over. */
const SEALED_ADDRESS_STATUSES = ['OUTBOUNDED', 'CLOSED', 'RETURNED', 'CANCELLED'];

/** Said on the pencil, so the refusal is met before the address is retyped rather than after. */
const describeAddressEditBlock = (fulfillmentStatus: string): string =>
  SEALED_ADDRESS_STATUSES.includes(fulfillmentStatus)
    ? `Delivery details are locked once the order is ${fulfillmentStatusLabel(fulfillmentStatus)}`
    : '';

/** The carrier's own consignee, edited beside the shipping address because that is what it ships to. */
const CONSIGNEE_FIELDS: AddressFieldSpec[] = [
  { key: 'name', label: 'Consignee Name' },
  { key: 'phone', label: 'Consignee Phone' },
  { key: 'address', label: 'Consignee Address' },
  { key: 'city', label: 'Consignee City' },
  { key: 'state', label: 'Consignee State' },
  { key: 'zipcode', label: 'Consignee Zipcode' },
  { key: 'countryCode', label: 'Consignee Country Code' },
];

/** FS-124: the timeline is the actor log, so persisted operation codes read as the action a person took. */
const ADMIN_OPERATION_LABELS: Record<string, string> = {
  ADMIN_HOLD: 'Order held',
  ADMIN_UNHOLD: 'Hold released',
  ADMIN_INBOUND: 'Inbounded',
  ADMIN_INBOUND_CANCEL: 'Inbound cancelled',
  ADMIN_LINE_CANCEL: 'Line cancelled',
  ADMIN_OUTBOUND: 'Outbounded',
  ADMIN_OUTBOUND_CANCEL: 'Outbound cancelled',
  ADMIN_ADDRESS_EDIT: 'Address edited',
  ADMIN_BULK_INBOUND: 'Bulk inbounded',
  LINE_ADDED: 'Line added',
  LINE_QUANTITY_CHANGED: 'Line quantity changed',
  LINE_REMOVED: 'Line removed',
  PIC_ASSIGNED: 'PIC assigned',
  TRACKING_CORRECTED: 'Tracking corrected',
  RESOLVE: 'Order resolved',
  CLOSE_UNSHIPPED: 'Order closed (never shipped)',
  UNRESOLVE: 'Resolved verdict lifted',
  RETURN_PUTAWAY: 'Return put away',
  ORDER_CLONED: 'Order cloned',
  ORDER_CREATED_BY_CLONE: 'Created as a clone',
  CARRIER_ORDER_FAILED: 'Carrier order failed',
};

const describeOperation = (operation: string): string => ADMIN_OPERATION_LABELS[operation] ?? operation;

/** A booking the server recorded as failed (SSD-7399): the carrier asked for, null when routed by country, and why. */
interface CarrierOrderFailure {
  provider: string | null;
  message: string;
}

/** The newest failed booking, so an order with no parcel says why it has none after the dialog that said so closed. */
const lastCarrierFailureOf = (events: FulfillmentEvent[]): CarrierOrderFailure | null => {
  const failed = [...events]
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
    .find((event) => event.operation === 'CARRIER_ORDER_FAILED');
  const payload = (failed?.payload ?? null) as Record<string, unknown> | null;
  const message = payload?.['message'];
  const provider = payload?.['provider'];
  if (typeof message !== 'string') {
    return null;
  }
  return { provider: typeof provider === 'string' ? provider : null, message };
};

/** Bootstrap variant per fulfillment status so list and detail read the same way. */
const statusVariant = (status: string): string => {
  if (status === 'INBOUNDED' || status === 'ACTIVE' || status === 'SHIPPED') {
    return 'success';
  }
  if (status === 'AWAITING_INBOUND' || status === 'READY') {
    return 'warning';
  }
  if (status === 'CANCELLED' || status === 'RELEASED') {
    return 'secondary';
  }
  return 'light';
};

/** Renders the catalog brand as a bold first token, per the wireframe's product-name contract. */
const BrandPrefixedName = ({
  brand,
  name,
}: {
  brand?: string | null | undefined;
  name?: string | null | undefined;
}) => {
  if (!name) {
    return <>—</>;
  }
  return (
    <>
      {brand && <b>{brand}</b>} {name}
    </>
  );
};

/**
 * The Korean catalog name usually already opens with the Korean brand, so prefixing the English brand
 * would print it twice. Bold that leading token instead and never mix the two spellings.
 */
const KoreanProductName = ({ name }: { name?: string | null | undefined }) => {
  if (!name) {
    return <>—</>;
  }
  const [head, ...rest] = name.split(' ');
  if (rest.length === 0) {
    return <>{name}</>;
  }
  return (
    <>
      <b>{head}</b> {rest.join(' ')}
    </>
  );
};

/** The WooCommerce spelling a recalculation may have replaced; kept permanently so cost reconciliation stays possible. */
const WooCommerceOriginal = ({
  original,
  current,
}: {
  original?: string | null | undefined;
  current?: string | null | undefined;
}) => (original && !sameOriginal(original, current) ? <div className="text-muted small">({original}*)</div> : null);

/**
 * [BR-46] A blocked control has to say why without being hovered: this bar is wide enough that a native
 * tooltip lands off-screen, and a `disabled` button never fires one at all in some browsers.
 */
/** Inline editor for one agent-filled field; commits on blur so a scan-speed operator never hunts for a save button. */

/** The agent buys in Korea, so the sourcing cost is won no matter what the customer was charged. */
const agentCostText = (cost?: number | null): string => (cost == null ? DASH : `₩${cost.toLocaleString('en-US')}`);
const catalogEditorUrl = (sku: string): string =>
  `${getConfig().adminUrl}/catalog/ss-editor?sku=${encodeURIComponent(sku)}`;

/**
 * A line shows its WooCommerce name, sku, size and quantity originals only when the variation-pack recalculation
 * changed it: the sku or the quantity. On an unchanged line those originals repeated the line with a size and pack
 * suffix (SSD-7267). The amount originals are not gated by this: a coupon or a sale leaves WooCommerce's own amount
 * apart from the one recalculated from the unit price on any line, so they always show beside it.
 */
const lineRecalculated = (line: FulfillmentOrderLine): boolean =>
  (line.originalSku != null && String(line.originalSku) !== String(line.sku)) ||
  (line.originalQuantity != null && Number(line.originalQuantity) !== Number(line.totalQuantity));

const formatOriginalAmount = (amount?: number | null): string | null => (amount == null ? null : String(amount));

/**
 * A recalculated line shows an original only where it differs from what the line now reads. A variation-pack split
 * usually keeps the amounts and often the quantity, and repeating them doubled the cell ("36.29 (36.29*) / 36.29
 * (36.29*)", four lines where two say it all, SSD-7674). Numbers compare as shown to the cent, text without outer
 * spaces.
 */
const sameOriginal = (original: string | number | null | undefined, current: string | number | null | undefined) => {
  if (original == null || current == null) {
    return false;
  }
  if (typeof original === 'number' || typeof current === 'number') {
    return Number(original).toFixed(2) === Number(current).toFixed(2);
  }
  return String(original).trim() === String(current).trim();
};
const showOriginal = (original: string | number | null | undefined, current: string | number | null | undefined) =>
  original != null && !sameOriginal(original, current);

/** The retired V1 mirror's reserved counter is a number, not a place, so it is never listed as a shelf. */
const V1_RESERVED_COUNTER = 'V1#RESERVED';

/** What a dash stands for when a line's units sit in V1's bucket; shown on hover and read by a screen reader. */
const NO_SHELF_YET = 'No shelf yet: V1 stock (WH#00)';
/** The same dash View Orders shows for it. */
const NO_SHELF_DASH = '–';

/**
 * The shelves a line's reservations hold, under its Inbound Status [SSD-7526]: one code alone, or each with its
 * quantity when the line is split. WH#00 is V1's bucket, not a shelf, so it is never named: a line held only there
 * shows a grey dash, as View Orders does and as labels leave it blank, and a split line shows that part as dash · qty.
 * It stays in the status cell rather than adding another column to the already scrollable table.
 */
const LineShelves = ({ locations }: { locations: FulfillmentLocationQty[] | undefined }) => {
  const held = (locations ?? []).filter(
    (location): location is FulfillmentLocationQty & { locationCode: string } =>
      Boolean(location.locationCode) && location.locationCode !== V1_RESERVED_COUNTER && location.qty > 0,
  );
  if (held.length === 0) {
    return null;
  }
  return (
    <span className="li-locs" aria-label="Location">
      {held.map((location, index) =>
        isV1WarehouseLocation(location.locationCode) ? (
          <span key={`v1-${index}`} className="li-loc li-loc-v1" title={NO_SHELF_YET} aria-label={NO_SHELF_YET}>
            {held.length > 1 ? `${NO_SHELF_DASH}\u00a0·\u00a0${location.qty}` : NO_SHELF_DASH}
          </span>
        ) : (
          <span key={`${location.locationCode}-${index}`} className="li-loc">
            {held.length > 1 ? `${location.locationCode}\u00a0·\u00a0${location.qty}` : location.locationCode}
          </span>
        ),
      )}
    </span>
  );
};

/** A JIT line prints the platform recorded for it [SSD-7058]; any other line prints its route as before. */
const orderLineRouteLabel = (line: FulfillmentOrderLine): string =>
  line.sourcingRoute ? lineRouteLabel(line.sourcingRoute, linePlatformLabel(line)) : DASH;

/** Marketing (1e9+) and internal (2e9+) orders answer 404 from the OMS, so they book a carrier the fulfillment way. */
const isBornInFulfillment2 = (orderId: number): boolean => orderId >= 1_000_000_000;

/** Snapshot urls are operator-editable, so only browser-navigable schemes may reach an href. */
const safeHref = (url?: string | null): string | null => {
  if (!url) {
    return null;
  }
  return /^https?:\/\//i.test(url) ? url : null;
};

const orderCurrencyOf = (amounts: unknown): string => {
  const currency = (amounts as { currency?: unknown } | null | undefined)?.currency;
  return typeof currency === 'string' && currency.length > 0 ? currency : 'USD';
};

const DASH = '—';

/** The line items table's column count (2 fixed + 5 product + 8 inventory + memo + actions), so the empty state can span it. */
const LINE_ITEM_COLUMNS = 17;
/**
 * Width of each line items column, in header order. Every px column is at least as wide as the longest single word it
 * holds in practice plus the cell's 5px padding each side, so no word is split across lines: a 14-digit CP order
 * number or waybill, "₩123,456", "1000ml", a five-digit stock count, an OUTBOUNDED badge, "WHOLESALE", a 10-digit
 * SKU with its copy button (measured in the browser, SSD-7267). The CP link shows its address cut short with an
 * ellipsis (SSD-7441). The Korean name takes a share of the table and the English name whatever is left, because it
 * is the longer one and gains most from room.
 *
 * A column Edit line puts a box in holds the same worst value inside that box, whose border and padding take 10px
 * more (SSD-7674): "1000ml", "100", a 14-digit order number or waybill, the order date with its calendar button, and
 * the platform select's longest short name ("Olive Young") with its arrow. The width is the same whether a row is
 * open or not, so opening Edit line moves no column. With Delivery Company the fixed columns add up to 1,315px;
 * below 1,511px the wrapper scrolls so the English name still has at least 100px.
 */
const LINE_ITEM_COLUMN_WIDTHS: ReadonlyArray<number | string> = [
  30, // select
  106, // SKU, with the thumbnail above it and the copy button beside the sku
  'auto', // Product Name: the rest of the table
  '6.3%', // Product Name KR
  67, // Size: "1000ml" in its edit box
  47, // Qty: "100" in its edit box
  60, // Subtotal / Total
  48, // Stock
  94, // Inbound Status
  107, // Sourcing Route: the platform select's "Olive Young" with its arrow
  133, // Order No. / Date: a 14-digit order number in its edit box
  72, // Product Cost
  133, // Tracking Number: a 14-digit waybill in its edit box
  156, // Delivery Company
  76, // CP Link
  102, // Memo
  84, // Actions
];

/**
 * The platform select's names in Edit line: "Brand official site" and "Other retailer" were cut to their first
 * letters in the Sourcing Route column ("Coup" on production), so the select shows a short name. The chosen
 * platform's full name is the select's own tooltip (SSD-7674). Everywhere else reads SOURCING_PLATFORM_LABELS.
 */
const EDIT_LINE_PLATFORM_LABELS: Record<SourcingPlatform, string> = {
  COUPANG: 'Coupang',
  NAVER: 'Naver',
  OLIVE_YOUNG: 'Olive Young',
  BRAND_OFFICIAL_SITE: 'Brand site',
  OTHER_RETAILER: 'Other shop',
};

/** Order ids at or above this are marketing imports WooCommerce never saw, so its actions do not apply to them. */
const MARKETING_BAND_FROM = 1_000_000_000;

const isWooCommerceOrder = (orderId: number): boolean => orderId < MARKETING_BAND_FROM;

/** Relabelling without the ledger's side effects would describe goods that never moved, so those are not offered. */
interface FulfillmentStatusChoice {
  status: string;
  /** The WooCommerce status that maps to it, for the statuses only the store can set. */
  wcStatus?: string;
  /** The warehouse action that produces it, for the two the warehouse owns outright. */
  action?: 'inbound' | 'outbound';
  /** Set by the operator alone, against no store or warehouse event; asks for a reason first. */
  internal?: 'resolve' | 'unresolve';
}

/** One vocabulary for the whole menu, so the operator never has to know which side of the house owns which word. */
const FULFILLMENT_STATUS_CHOICES: FulfillmentStatusChoice[] = [
  { status: 'INBOUNDED', action: 'inbound' },
  // READY means every line is in the warehouse. prepare-shipment now starts at the first purchase (SSD-7450), so no
  // store status maps to READY any more: only 2.0's own status route sets it.
  { status: 'READY' },
  { status: 'OUTBOUNDED', wcStatus: 'shipped', action: 'outbound' },
  { status: 'CLOSED', wcStatus: 'completed' },
  { status: 'RETURNED', wcStatus: 'refunded' },
  { status: 'CANCELLED', wcStatus: 'cancelled' },
  // Internal, post-shipment: the order ended outside the pipeline, so no store or warehouse event reaches it.
  { status: 'RESOLVED', internal: 'resolve' },
];

/** The menu statuses V2's status route sets once Fulfillment 2.0 has launched; CANCELLED goes to the cancel flow. */
const isFulfillmentMenuStatus = (status: string): status is FulfillmentMenuStatus =>
  status === 'READY' || status === 'CLOSED' || status === 'RETURNED';

const GENERIC_ACTION_FAILURE = 'The action failed. Reload the page and try again.';

/**
 * A write refused because the row moved under it. The screen reloads, so the sentence has to say that
 * it did — an operator who is not told the screen changed retypes the edit onto the new version [T03].
 */
/**
 * Refusals that still changed the order: a reset whose new carrier order failed after the old one was
 * cancelled, or whose rows moved under it. The screen would show a waybill that is gone, so it is re-read.
 */
const ORDER_CHANGING_REFUSALS = new Set([
  'CONFLICT',
  // The lock can land between the read and the cancel (SSD-7450); re-reading shows its badge beside the refusal.
  CANCEL_LOCKED_CODE,
  'SHIPMENT_CANCELLATION_RECONCILIATION_REQUIRED',
  'SHIPMENT_PROVIDER_VALIDATION_FAILED',
  'SHIPMENT_PROVIDER_FAILED',
  'SHIPMENT_RECONCILIATION_REQUIRED',
]);

/** Reset and Create answer the same 409 as the switch on a web order while the launch switch is off (SSD-7060). */
const describeWebOrderBeforeLaunch =
  (action: string) =>
  (_message: string | string[] | undefined, body?: AdminActionErrorBody): string | undefined =>
    body?.code === 'WEB_ORDER_BEFORE_LAUNCH' ? webOrderBeforeLaunch(action) : undefined;

/** A cancel that wrote a note says where it went (SSD-7643): the server posted it to the order comments. */
const cancelledMessage = (orderId: number, note?: string): string =>
  note === undefined
    ? `Order ${orderId} cancelled.`
    : `Order ${orderId} cancelled. The note is posted to its comments.`;

/**
 * A change of mind refused for the lock (SSD-7450). The dialog asks why on any locked order (SSD-7643), so this only
 * happens when the lock landed after the screen was read; the order is re-read and the next dialog asks.
 */
const describeCancelLocked = (
  _message: string | string[] | undefined,
  body?: AdminActionErrorBody,
): string | undefined => {
  if (body?.code !== CANCEL_LOCKED_CODE) {
    return undefined;
  }
  const at = kstMonthDayMinute(body.cancelLockedAt);
  const source = cancelLockSourceLabel(body.cancelLockSource);
  const lock = at === null ? '' : ` at ${at}${source ? ` (${source})` : ''}`;
  return (
    `The order was locked${lock} after this screen loaded, so it needs a reason to cancel. ` +
    'The order was reloaded: open Cancel Order again and write why in the box.'
  );
};

/**
 * How the web-order cancel words a refusal (SSD-7675). A shipped order is told the way back, and any other refusal the
 * order route explains keeps its own sentence: the generic one hid "cancel the outbound first" on 472678 and 472680.
 */
const describeOrderCancelRefusal = (
  message: string | string[] | undefined,
  body?: AdminActionErrorBody,
): string | undefined => {
  const locked = describeCancelLocked(message, body);
  if (locked !== undefined) {
    return locked;
  }
  if (body?.code === 'ORDER_ALREADY_SHIPPED') {
    const shipped = body.shippedLines ?? [];
    const named = shipped.map((line) => `line ${line.lineNo} is ${fulfillmentStatusLabel(line.lineStatus)}`).join(', ');
    const wayBack = shipped.some((line) => line.lineStatus === 'CLOSED')
      ? 'A closed shipment can only come back as a return.'
      : 'Cancel the outbound first, then cancel the order.';
    return `This order has already shipped${named ? ` (${named})` : ''}, so it cannot be cancelled. ${wayBack}`;
  }
  const text = Array.isArray(message) ? message.join(' · ') : message;
  return text || undefined;
};

/**
 * The JSON body an order-route refusal carried. Only the order cancel attaches one (SSD-7450), so every other OMS
 * failure reads as before: no body, the generic sentence.
 */
const omsRefusalBody = (err: unknown): AdminActionErrorBody | undefined =>
  err instanceof OMSRequestError && typeof err.body === 'object' && err.body !== null
    ? (err.body as AdminActionErrorBody)
    : undefined;

const describeStaleWrite = (
  failure: { response?: { status?: number; data?: AdminActionErrorBody } } | undefined,
): string | undefined => {
  const body = failure?.response?.data;
  if (failure?.response?.status !== 409 || body?.code !== 'CONFLICT') {
    return undefined;
  }
  const who = body.changedBy ? `changed by ${body.changedBy}` : 'changed by someone else';
  return `This order was ${who} — reloaded, check it and try again.`;
};

/**
 * What the menu entry does, rather than the status it is filed under. Picking "INBOUNDED" inbounds
 * every open line and lands the order at READY, so the old label named neither the action nor the result.
 */
const describeStatusChoice = (choice: FulfillmentStatusChoice): string => {
  if (choice.internal === 'unresolve') {
    return 'RESOLVED — lift verdict';
  }
  if (choice.action === 'inbound') {
    return 'Inbound every open line';
  }
  return fulfillmentStatusLabel(choice.status);
};

/**
 * A web order cancels from processing, and from prepare-shipment: the first purchase moves it there and locks it
 * (SSD-7450), and our side must still be able to cancel it then; the server refuses a shipped one and a change of mind
 * asked after the lock. Anywhere else the action would buy an opaque failure, so it is not offered.
 */
const CANCELLABLE_FROM: readonly string[] = [
  WooCommerceOrderStatus.PROCESSING,
  WooCommerceOrderStatus.PREPARE_SHIPMENT,
];
/**
 * The shop's status lags the cancel by a few seconds since the shop push no longer holds the screen (SSD-7643), so an
 * order 2.0 already reads CANCELLED says so instead of offering the cancel again.
 */
const describeCancelBlock = (
  wcStatus: string | null,
  fulfillmentStatus?: string,
  lines: readonly FulfillmentOrderLine[] = [],
): string => {
  if (fulfillmentStatus === 'CANCELLED') {
    return 'This order is already cancelled';
  }
  // A closed shipment's outbound can no longer be cancelled, so the server's only way back is a return (SSD-7675).
  if (lines.some((line) => line.lineStatus === 'CLOSED')) {
    return 'This order has shipped and closed, so it can only come back as a return';
  }
  return wcStatus !== null && CANCELLABLE_FROM.includes(wcStatus)
    ? ''
    : `Only an order in processing or prepare-shipment can be cancelled (this one is ${wcStatus ?? 'unknown'})`;
};

/** Cancelling in fulfillment is the only way an order WooCommerce never saw ever lets its reservations go. */
const describeFulfillmentCancelBlock = (fulfillmentStatus: string, lines: FulfillmentOrderLine[]): string => {
  if (fulfillmentStatus === 'CANCELLED') {
    return 'This order is already cancelled';
  }
  return lines.some((line) => ['OUTBOUNDED', 'CLOSED'].includes(line.lineStatus))
    ? 'Cancel Outbound first — this order has already shipped'
    : '';
};

/** Require the server's eligibility flag; legacy payloads must never guess that stock can be removed. */
const hasWritableReceivedGoods = (line: FulfillmentOrderLine): boolean =>
  line.inboundReversible === true && canCancelInbound(line.lineStatus, line.inboundReversible);

const OUTBOUND_FIRST = 'Cancel Outbound first';
const LINE_CANCELLED = 'This line is cancelled';
const RELOADING = 'Waiting for the order to reload';
const FIX_LINE_FIELDS_FIRST = 'Fix the highlighted fields first';

/**
 * The row menu is laid out against the viewport. The line table scrolls sideways, which clips whatever
 * is laid out inside it, and on a short order that cut the last row's menu off below the table.
 * The `line-menu` class makes it fixed before Popper first measures it; see orderDetail.css.
 */
const ROW_MENU_POPPER = { strategy: 'fixed' } as const;

/**
 * An action that vanishes reads as a broken screen, so every line action stays on the row greyed out
 * with its gate named. These answer why one is refused; an empty string means it is allowed.
 */
const describeLineInboundBlock = (lineStatus: string): string => {
  if (canInboundLine(lineStatus)) {
    return '';
  }
  if (lineStatus === 'OUTBOUNDED') {
    return OUTBOUND_FIRST;
  }
  if (lineStatus === 'CANCELLED') {
    return LINE_CANCELLED;
  }
  return isOutboundReady(lineStatus) ? 'This line is already inbounded' : `A ${lineStatus} line cannot be inbounded`;
};

const describeCancelInboundBlock = (lineStatus: string, inboundReversible?: boolean): string => {
  if (canCancelInbound(lineStatus, inboundReversible)) {
    return '';
  }
  if (lineStatus === 'OUTBOUNDED') {
    return OUTBOUND_FIRST;
  }
  if (lineStatus === 'CANCELLED') {
    return LINE_CANCELLED;
  }
  return isOutboundReady(lineStatus) ? 'This arrival cannot be placed back' : 'This line has no arrival to take back';
};

const describeCancelLineBlock = (lineStatus: string): string => {
  if (canCancelLine(lineStatus)) {
    return '';
  }
  if (lineStatus === 'OUTBOUNDED') {
    return OUTBOUND_FIRST;
  }
  return lineStatus === 'CANCELLED' ? 'This line is already cancelled' : `A ${lineStatus} line cannot be cancelled`;
};

/** Why a line's sourcing detail can only be read: the server records it on JIT lines, and a cancelled line is shut. */
const sourcingReadOnlyReason = (line: FulfillmentOrderLine): string | undefined => {
  if (line.sourcingRoute !== 'JIT') {
    return `Sourcing detail is recorded on JIT lines only; this line is ${line.sourcingRoute ?? 'not routed'}.`;
  }
  return line.lineStatus === 'CANCELLED' ? LINE_CANCELLED : undefined;
};

/**
 * A shipped line still opens Edit line: the purchase that sourced it is often recorded after the parcel left,
 * and the agent-tracking write takes any line status. Only a cancelled line is shut.
 */
const describeLineEditBlock = (lineStatus: string): string => (lineStatus === 'CANCELLED' ? LINE_CANCELLED : '');

/** Changing the quantity and removing both rewrite the line, so they share the cancellable set the stock ledger can follow. */
const describeLineQuantityBlock = (lineStatus: string): string => {
  if (canCancelLine(lineStatus)) {
    return '';
  }
  if (lineStatus === 'OUTBOUNDED') {
    return OUTBOUND_FIRST;
  }
  if (lineStatus === 'CLOSED') {
    return 'This line has shipped and is closed';
  }
  return lineStatus === 'CANCELLED' ? LINE_CANCELLED : `A ${lineStatus} line keeps its quantity`;
};

const editableLineErrorsOf = (
  line: FulfillmentOrderLine,
  draft: LineDraft,
  stored: LineDraft,
  refusals: LineEditErrors = {},
): LineEditErrors => {
  const errors = { ...refusals, ...lineEditErrorsOf(draft, stored) };
  if (describeLineQuantityBlock(line.lineStatus) !== '') {
    delete errors.totalQuantity;
  }
  return errors;
};

/**
 * Unified shipping states a parcel can only reach after a carrier picked it up (the backend's
 * UnifiedShippingStatusTransition: each is PICKED_UP or comes after it). Anything else, including a blank or unknown
 * status, is not evidence the goods left. CANCELLED, EXPIRED and EXCEPTION stay out on purpose: each can follow
 * ORDER_PLACED directly, before pickup. Because only the current status is read, a parcel that went
 * PICKED_UP -> EXCEPTION/CANCELLED also stays shut here [SSD-7566].
 */
const PICKED_UP_TRACKING_STATES: ReadonlySet<string> = new Set([
  'PICKED_UP',
  'IN_TRANSIT',
  'CUSTOMS_CLEARANCE',
  'CUSTOMS_HOLD',
  'ARRIVED_AT_DESTINATION',
  'HANDED_TO_LOCAL_CARRIER',
  'LOCAL_IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'HELD_FOR_PICKUP',
  'DELIVERED',
  'FAILED_ATTEMPT',
  'RETURNING',
  'RETURNED',
  'LOST',
]);

/** Whether a carrier has affirmatively reported the order's outbound parcel picked up, i.e. the goods actually left us. */
const outboundParcelLeft = (outbound: FulfillmentTracking[]): boolean =>
  outbound.some((shipment) => PICKED_UP_TRACKING_STATES.has((shipment.unifiedStatus ?? '').trim().toUpperCase()));

/**
 * The states a line is put back on a shelf from: one that went out, and one a refund marked RETURNED after it had gone
 * (orders 460476 and 464666, refunded while the parcel was on its way back). A refund also marks RETURNED a line that
 * never left, whose goods are still on the shelf, so a RETURNED line opens only once a carrier has reported the
 * order's outbound parcel picked up. A line already put away is refused by the server's returnInfo [SSD-7566].
 */
const describeReturnPutawayBlock = (lineStatus: string, parcelLeft: boolean): string => {
  if (lineStatus === 'OUTBOUNDED') {
    return '';
  }
  if (lineStatus === 'RETURNED') {
    return parcelLeft ? '' : 'No carrier has reported this parcel picked up, so its goods may still be on the shelf';
  }
  return 'Only an outbounded or returned line can be put back on the shelf';
};

const readRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/** A plain order number; the legacy V1 clone form (`446895-1`) is not one and must not be shown. */
const ORDER_NUMBER_PATTERN = /^[1-9]\d*$/;

/**
 * The order this one was cloned from, read only from the persisted `clone_of` segment. Anything that is
 * not exactly one well-formed source order yields null, so a malformed row never invents a lineage.
 */
const originalOrderIdOf = (segments: unknown): string | null => {
  if (!Array.isArray(segments)) {
    return null;
  }
  const sources = new Set<string>();
  for (const segment of segments) {
    const { type, order_id: orderId } = readRecord(segment);
    if (type !== 'clone_of') {
      continue;
    }
    const source = typeof orderId === 'number' ? String(orderId) : typeof orderId === 'string' ? orderId.trim() : '';
    if (ORDER_NUMBER_PATTERN.test(source)) {
      sources.add(source);
    }
  }
  return sources.size === 1 ? [...sources][0]! : null;
};

/** `#2000000202 (455345-1)`: the 2.0 number the link opens, and the V1 OMS id ops know a V1 clone by. */
const withV1OrderId = (orderId: number | string, v1OrderId: string | null | undefined): string =>
  v1OrderId ? `#${orderId} (${v1OrderId})` : `#${orderId}`;

/**
 * The order this one was cloned from. The server's own link wins; a server that predates it still sent
 * the segment, so the page reads that rather than dropping a link it used to show.
 */
const cloneSourceOf = (detail: FulfillmentOrderDetail): string | null => {
  const linked = detail.relations?.cloneOf?.orderId;
  return linked != null && ORDER_NUMBER_PATTERN.test(String(linked))
    ? String(linked)
    : originalOrderIdOf(detail.header.segments);
};

const textOf = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim() === '' ? DASH : value;
  }
  return typeof value === 'number' ? String(value) : DASH;
};

const moneyOf = (value: unknown, currency: string): string =>
  typeof value === 'number' ? `${currency} ${value}` : DASH;

/** WooCommerce spells an address one way; the card reads it back as the slot's rows, optional ones only once set. */
const addressFields = (address: unknown, fields: AddressFieldSpec[]): FactField[] => {
  const source = readRecord(address);
  return fields
    .map(({ key, label, hiddenWhenEmpty }) => ({ label, value: textOf(source[key]), hiddenWhenEmpty }))
    .filter(({ value, hiddenWhenEmpty }) => !(hiddenWhenEmpty && value === DASH))
    .map(({ label, value }) => ({ label, value }));
};

/** The stored value of every editable row, as text — an absent key edits as an empty box, not as `—`. */
const addressDraftOf = (address: unknown, fields: AddressFieldSpec[]): Record<string, string> => {
  const source = readRecord(address);
  return Object.fromEntries(fields.map(({ key }) => [key, source[key] == null ? '' : String(source[key])]));
};

/** Only what the operator actually retyped travels; an untouched row must not overwrite the stored one. */
const changedFieldsOf = (draft: Record<string, string>, stored: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(draft).filter(([key, value]) => value !== stored[key]));

/** Spec [E-39]: a print that never reaches the printer must not be confirmed as sent. */
const PRINT_FAILED_TOAST = 'Label not printed — print it again';

/** The archive holds no pdf it can print for this parcel, so there is nothing to send to the printer. */
const NO_ARCHIVED_LABEL_TOAST = 'No printable carrier label is archived for this parcel — label not printed';

const StatusBadge = ({ status }: { status: string }) => {
  const variant = statusVariant(status);
  const onLightSurface = variant === 'warning' || variant === 'light';
  return (
    <Badge bg={variant} text={onLightSurface ? 'dark' : 'light'}>
      {String(status ?? '').replace(/_/g, ' ')}
    </Badge>
  );
};

/**
 * One cell of the open row: the box, and under it the sentence standing against its value. The reason
 * sits with the field because a toast at the foot of the screen names no box on a row this wide.
 */
const LineEditCell = ({
  label,
  value,
  error,
  onChange,
  type = 'text',
  alignEnd = false,
  disabledReason = '',
}: {
  label: string;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
  type?: 'text' | 'date';
  alignEnd?: boolean;
  /** Shows the field greyed out with the reason under it; empty means it is editable. */
  disabledReason?: string;
}) => (
  <>
    <Form.Control
      size="sm"
      type={type}
      disabled={disabledReason !== ''}
      title={disabledReason || undefined}
      // A date input takes a six-digit year unless it is told where the calendar stops.
      {...(type === 'date' ? { max: '9999-12-31' } : {})}
      {...(alignEnd ? { className: 'text-end' } : {})}
      aria-label={label}
      aria-invalid={error != null}
      isInvalid={error != null}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    {/* A blocked control names its gate on screen, not only on hover. */}
    {!error && disabledReason && <div className="text-muted small">{disabledReason}</div>}
  </>
);

/** The open row's values as the editor first read them. */
interface LineEditBase {
  draft: LineDraft;
  updatedAt: string | null | undefined;
  parcels?: LineTrackingCourier[] | undefined;
  targetId?: string | undefined;
  adding?: boolean;
  /**
   * The row opened ready for a waybill because it has no parcel, not because + Add waybill was pressed (SSD-7814).
   * A company picked with no number is still the line's pre-waybill choice here; after + Add it belongs to the new
   * waybill and waits for its number.
   */
  preWaybill?: boolean;
}

/** The line whose arrival is being taken back, and how many units that arrival booked. */
interface CancelInboundTarget {
  lineNo: number;
  sku: string;
  arrivedQty: number;
}

export default function Fulfillment2DetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  // Read from the route, not the loaded order, so the call sits above the page's loading returns (SSD-7372).
  const packingVideos = usePackingVideos(client, orderId ?? null);
  const stockClient = useMemo(() => new StockClient(), []);
  const oms = useOMS();
  const { user } = useAuth();
  const [detail, setDetail] = useState<FulfillmentOrderDetail | null>(null);
  const gigWorkerClient = useMemo(
    () => new GigWorkerClient(getAdminApiUrl(AdminApiDomain.GIGWORK), getConfig().apiKey),
    [],
  );
  // Migrated V1 clones keep their persisted OMS reference; V2-born orders use their full numeric id.
  const gigOmsOrderRef =
    detail && String(detail.header.orderId) === orderId
      ? (detail.relations?.v1OrderId ?? String(detail.header.orderId))
      : null;
  const [openGigOrderId, setOpenGigOrderId] = useState<string | null>(null);
  const {
    data: gigOrderReference,
    isError: isGigOrderLookupError,
    refetch: refetchGigOrderReference,
  } = useQuery({
    queryKey: ['gig-order-reference-by-oms', gigOmsOrderRef],
    queryFn: () => gigWorkerClient.getOrderReferenceByOmsOrderRef(gigOmsOrderRef!),
    enabled: Boolean(gigOmsOrderRef),
    retry: 2,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    setOpenGigOrderId(null);
  }, [orderId]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  /** Bumped only by the tab coming back; a write re-reads the order through runAction already [D-p]. */
  const [refocusToken, setRefocusToken] = useState(0);
  const [revalidating, setRevalidating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelInboundTarget, setCancelInboundTarget] = useState<CancelInboundTarget | null>(null);
  /** The order line whose split dialog is open (SSD-7377), and the server's last refusal of that split. */
  const [splitTarget, setSplitTarget] = useState<{ orderId: number; lineNo: number } | null>(null);
  const [splitRefusal, setSplitRefusal] = useState<string | undefined>(undefined);
  /** The line whose "No stock → awaiting purchase" dialog is open (SSD-7506), and the server's last refusal of it. */
  const [toJitTarget, setToJitTarget] = useState<{ orderId: number; lineNo: number } | null>(null);
  const [toJitRefusal, setToJitRefusal] = useState<string | undefined>(undefined);
  // Freeze the line version at dialog open; a background refresh must not silently change the operator's ask.
  const [existingStockTarget, setExistingStockTarget] = useState<{
    orderId: number;
    line: FulfillmentOrderLine;
  } | null>(null);
  const [existingStockRefusal, setExistingStockRefusal] = useState<string | undefined>(undefined);
  const existingStockKeysRef = useRef(new AttemptKeys());
  /** The line whose seller type and price comparison dialog is open (SSD-7177). */
  const [sourcingLineNo, setSourcingLineNo] = useState<number | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [editingPic, setEditingPic] = useState(false);
  const [editingTracking, setEditingTracking] = useState(false);
  /** The other order the saved waybill also names; the toast is fleeting, so the card keeps saying it. */
  const [trackingInUse, setTrackingInUse] = useState<TrackingInUseWarning | null>(null);
  const [selectedLineNos, setSelectedLineNos] = useState<number[]>([]);
  const inboundableRef = useRef<number[]>([]);
  const allInboundLineNosRef = useRef<number[]>([]);
  useEffect(() => {
    allInboundLineNosRef.current = (detail?.lines ?? []).map((line) => line.lineNo);
    inboundableRef.current = (detail?.lines ?? [])
      .filter((line) => canInboundLine(line.lineStatus))
      .map((line) => line.lineNo);
  }, [detail]);
  const { ask, askConfirm, askChoice, askBinaryChoice, dialog: promptDialog } = usePromptDialog();
  const csActionRef = useRef(false);
  const { askReceivedGoods, dialog: receivedGoodsDialog } = useReceivedGoodsCancelDialog();
  // The COGS report is a message to the catalog team, not a change to the order, so it needs no refresh (SSD-7328).
  const [cogsReportOpen, setCogsReportOpen] = useState(false);
  // So is a catalog request (SSD-7384): stock status, sourcing link or product info about one line.
  const [catalogRequestOpen, setCatalogRequestOpen] = useState(false);
  const askCancellation = async (
    title: string,
    confirmLabel: string,
    canWriteOff: boolean,
    maxReasonLength: number,
  ): Promise<CancelLineRequest | null> => {
    if (canWriteOff) {
      return askReceivedGoods({ title, confirmLabel, maxReasonLength });
    }
    const reason = await ask({ title, label: 'Reason', confirmLabel });
    return reason === null ? null : { reason };
  };
  /**
   * The order cancel always asks whose reason it is, and for a change of mind when the customer asked (SSD-7450);
   * the order-level stock choice joins it only where received goods can be written off. `withGoods` adds the
   * per-line dispositions the order route persists.
   */
  const askOrderCancellation = (canWriteOff: boolean, withGoods = false): Promise<CancelAnswer | null> =>
    askReceivedGoods({
      title: 'Cancel order',
      confirmLabel: 'Cancel order',
      maxReasonLength: 200,
      showReceivedGoods: canWriteOff,
      askCancelType: true,
      lock: cancelLockOf(detail?.header),
      ...(withGoods && { goodsLines: cancelGoodsLinesOf(detail?.lines ?? []) }),
    });
  const { toasts, showSuccess, showError, showWarning, dismiss: dismissToast } = useFulfillmentToast();
  const launch = useFulfillmentV2Launch();

  /** Every sheet this order prints goes through the one dispatcher, which prints it at once. */
  const printer = useMemo(() => new PrintDispatcher(), []);
  const [editingLineNo, setEditingLineNo] = useState<number | null>(null);
  const [lineDraft, setLineDraft] = useState<LineDraft>(EMPTY_LINE_DRAFT);
  /**
   * The row as the editor opened it. A refresh while the editor is open must not become the save's baseline:
   * another operator's change would then pass the version check and be overwritten by this draft [T05].
   */
  const [lineEditBase, setLineEditBase] = useState<LineEditBase | null>(null);
  /** What the server refused on the open row, per field; cleared the moment the operator edits that field. */
  const [lineRefusals, setLineRefusals] = useState<LineEditErrors>({});
  const [editingAddress, setEditingAddress] = useState<AddressSlot | null>(null);
  const [addressDraft, setAddressDraft] = useState<AddressDraft>(EMPTY_ADDRESS_DRAFT);
  /** What the server refused, per field; cleared the moment the operator edits that field. */
  const [addressRefusals, setAddressRefusals] = useState<Record<string, string>>({});
  const [stockBySku, setStockBySku] = useState<Record<string, number>>({});
  const [picProfiles, setPicProfiles] = useState<PicProfile[]>([]);
  const [selectedPic, setSelectedPic] = useState('');
  const [comments, setComments] = useState<OrderComment[]>([]);
  const [commentText, setCommentText] = useState('');
  /** What is typed in each line's Memo box, by line number; a draft on line 2 stays while line 3 is written. */
  const [memoDrafts, setMemoDrafts] = useState<Record<number, string>>({});
  /** One key per (order, line, text) ask, kept across a failed try so a retry cannot write the memo twice (SSD-7265). */
  const memoKeysRef = useRef(new AttemptKeys());
  const [addingLine, setAddingLine] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [bookingCarrier, setBookingCarrier] = useState(false);
  /** Open while the switch dialog is up; holds the carrier order read for it, or null when it resumes a kept switch. */
  const [switching, setSwitching] = useState<{ source: SwitchCarrierSource | null } | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  /** The Shelf box, as View Orders keeps it: what is typed, whether it is unsaved, and whether the last save landed. */
  const [shelf, setShelf] = useState('');
  const [shelfDirty, setShelfDirty] = useState(false);
  const [shelfSaved, setShelfSaved] = useState(false);
  /** The order whose Shelf is being typed into; a reload of that order leaves the typing alone. */
  const shelfEditRef = useRef<number | null>(null);
  /** Counts keystrokes into the Shelf, so a save settles the box only when nothing was typed while it ran. */
  const shelfTypingRef = useRef(0);
  /** Deleo-family stickers, rendered off screen so the document can read their markup. */
  const deleoLabelRef = useRef<HTMLDivElement>(null);
  const loadedOrderIdRef = useRef<string>();
  const revalidationCountRef = useRef(0);
  const csWriteRef = useRef<{ orderId: number | null; revision: number }>({ orderId: null, revision: 0 });
  const refocusRevalidationRef = useRef(false);
  const pendingCommentRef = useRef<{ text: string; idempotencyKey: string } | null>(null);
  const pendingAddLineRef = useRef<{ fingerprint: string; idempotencyKey: string } | null>(null);
  const putawayKeysRef = useRef(new AttemptKeys());
  /** A clone retried with the same ask reuses its key, so a request that did land is never cloned twice. */
  const cloneKeysRef = useRef(new AttemptKeys());
  /** A split retried with the same parts reuses its key, so a split that did land is answered, not applied twice. */
  const splitKeysRef = useRef(new AttemptKeys());
  /** A switch to purchase retried with the same ask reuses its key, so units are never taken off the shelf twice. */
  const toJitKeysRef = useRef(new AttemptKeys());
  /** setBusy(true) only shuts the button on the next render, which is a tick too late for a double click. */
  const inFlightRef = useRef(false);
  /** Set by the failure branch of the last write, read back by whichever form submitted it. */
  const lastAddressRefusalRef = useRef<Record<string, string>>({});
  const lastLineRefusalRef = useRef<LineEditErrors>({});

  /** A pre-write read may refresh other fields, but cannot roll back this order's confirmed CS value. */
  const applyDetailReadWithCsGuard = useCallback((data: FulfillmentOrderDetail, csRevisionAtRead: number) => {
    setDetail((current) => {
      const write = csWriteRef.current;
      if (
        current?.header.orderId === data.header.orderId &&
        write.orderId === data.header.orderId &&
        write.revision !== csRevisionAtRead
      ) {
        return {
          ...data,
          header: {
            ...data.header,
            csStatus: current.header.csStatus ?? null,
            csResolutionMethod: current.header.csResolutionMethod ?? null,
          },
        };
      }
      return data;
    });
  }, []);

  const beginDetailRevalidation = useCallback(() => {
    revalidationCountRef.current += 1;
    setRevalidating(true);
  }, []);

  const finishDetailRevalidation = useCallback(() => {
    revalidationCountRef.current -= 1;
    if (revalidationCountRef.current === 0) {
      setRevalidating(false);
    }
  }, []);

  /** OMS names the tab after the order, so an operator with both screens open can tell them apart. */
  useEffect(() => {
    if (orderId) {
      document.title = `${orderId} - Order Detail`;
    }
  }, [orderId]);

  useEffect(() => {
    if (
      editingLineNo !== null &&
      detail?.lines.find((line) => line.lineNo === editingLineNo)?.lineStatus === 'CANCELLED'
    ) {
      setEditingLineNo(null);
      setLineRefusals({});
    }
  }, [detail, editingLineNo]);

  /**
   * An order moves while the operator is on another tab, so it is re-read on the way back rather than
   * polled — the screen then states what the stores actually hold, not what they held on arrival [D-p].
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        if (!refocusRevalidationRef.current) {
          refocusRevalidationRef.current = true;
          beginDetailRevalidation();
        }
        setRefocusToken((token) => token + 1);
        setReloadToken((token) => token + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [beginDetailRevalidation, orderId]);

  useEffect(() => {
    if (!orderId) {
      return;
    }
    if (loadedOrderIdRef.current !== orderId) {
      loadedOrderIdRef.current = orderId;
      setDetail(null);
      setLoading(true);
      setComments([]);
      setSelectedPic('');
      setCommentText('');
      pendingCommentRef.current = null;
      setMemoDrafts({});
      memoKeysRef.current = new AttemptKeys();
      pendingAddLineRef.current = null;
      setAddingLine(false);
      setCloning(false);
      setTrackingNumber('');
      setTrackingCarrier('');
      setStockBySku({});
      setSelectedLineNos([]);
      setEditingLineNo(null);
      setLineDraft(EMPTY_LINE_DRAFT);
      setLineRefusals({});
      setEditingAddress(null);
      setAddressDraft(EMPTY_ADDRESS_DRAFT);
      setSplitTarget(null);
      setSplitRefusal(undefined);
      setToJitTarget(null);
      setToJitRefusal(undefined);
      setExistingStockTarget(null);
      setExistingStockRefusal(undefined);
    }
    const numId = Number(orderId);
    if (isNaN(numId)) {
      if (refocusRevalidationRef.current) {
        refocusRevalidationRef.current = false;
        finishDetailRevalidation();
      }
      setLoading(false);
      setError(true);
      return;
    }
    let ignore = false;
    const csRevisionAtRead = csWriteRef.current.revision;
    setNotFound(false);
    setError(false);
    client
      .getOrderDetailForOutbound(numId)
      .then((data) => {
        if (!ignore) {
          applyDetailReadWithCsGuard(data, csRevisionAtRead);
          setSelectedPic(data.header.picUser ?? '');
        }
      })
      .catch((err: { response?: { status?: number } }) => {
        if (ignore) {
          return;
        }
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(true);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
          if (refocusRevalidationRef.current) {
            refocusRevalidationRef.current = false;
            finishDetailRevalidation();
          }
        }
      });
    return () => {
      ignore = true;
    };
  }, [orderId, client, finishDetailRevalidation, refocusToken, applyDetailReadWithCsGuard]);

  const shelfOrderId = detail?.header.orderId;
  const storedShelf = detail?.header.temporaryLocation ?? '';
  useEffect(() => {
    if (shelfEditRef.current === shelfOrderId) {
      return;
    }
    shelfEditRef.current = null;
    setShelf(storedShelf);
    setShelfDirty(false);
  }, [shelfOrderId, storedShelf]);
  useEffect(() => {
    setShelfSaved(false);
  }, [shelfOrderId]);

  // FS-135 until the read repo joins stock itself: supplementary, so a failure must not break the lines.
  useEffect(() => {
    const skus = [...new Set((detail?.lines ?? []).map((line) => line.sku))];
    if (skus.length === 0) {
      return;
    }
    let ignore = false;
    stockClient
      .getStockStatus(skus)
      .then((status) => {
        if (ignore) {
          return;
        }
        const counts: Record<string, number> = {};
        for (const [sku, stock] of Object.entries(status.productStocks ?? {})) {
          counts[sku] = (stock?.totalQuantity ?? 0) - (stock?.totalReservedQuantity ?? 0);
        }
        setStockBySku(counts);
      })
      .catch(() => {
        if (!ignore) {
          setStockBySku({});
        }
      });
    return () => {
      ignore = true;
    };
  }, [detail, stockClient]);

  useEffect(() => {
    const numericOrderId = Number(orderId);
    if (!Number.isInteger(numericOrderId) || numericOrderId <= 0) {
      return;
    }
    let ignore = false;
    client
      .getPicRoster()
      .then((roster) => {
        if (ignore) {
          return;
        }
        setPicProfiles(roster.pics);
      })
      .catch(() => {
        if (!ignore) {
          showError('Could not load the PIC roster.');
        }
      });
    client
      .listOrderComments(numericOrderId)
      .then((thread) => {
        if (!ignore) {
          setComments(thread.comments);
        }
      })
      .catch(() => {
        if (!ignore) {
          setComments([]);
          showError('Could not load the order comments.');
        }
      });
    return () => {
      ignore = true;
    };
  }, [client, orderId, reloadToken, showError]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" aria-label="Loading order" />
      </div>
    );
  }
  if (notFound) {
    return (
      <Container fluid className="py-3">
        <Alert variant="warning">
          Order not found. Check the order id, or go back to the{' '}
          <Alert.Link as={Link} to={ROUTES.fulfillment.orders}>
            order list
          </Alert.Link>
          .
        </Alert>
      </Container>
    );
  }
  if (error) {
    return (
      <Container fluid className="py-3">
        <Alert variant="danger">Could not load this order. Reload the page to try again.</Alert>
      </Container>
    );
  }
  if (!detail) {
    return null;
  }

  const { header, lines, reservations, tracking, events, pendingParcels } = detail;
  const orderCurrency = orderCurrencyOf(header.amounts);
  /** Shared by the button and its handler, so neither can print a line that has not arrived. */
  const packingLabelRefusal =
    header.fulfillmentStatus === 'CANCELLED'
      ? 'Print on a cancelled order is blocked'
      : describePackingLabelBlock(lines.map((line) => line.lineStatus));

  /** Re-reads the order so the screen states what the stores actually hold, whatever the action reported. */
  const refreshDetail = async (expectedWooCommerceStatus?: string): Promise<boolean> => {
    const targetOrderId = header.orderId;
    const csRevisionAtRead = csWriteRef.current.revision;
    beginDetailRevalidation();
    try {
      const refreshed = await client.getOrderDetailForOutbound(targetOrderId);
      if (loadedOrderIdRef.current === String(targetOrderId)) {
        applyDetailReadWithCsGuard(refreshed, csRevisionAtRead);
        setSelectedPic(refreshed.header.picUser ?? '');
      }
      return expectedWooCommerceStatus === undefined || refreshed.header.wcStatus === expectedWooCommerceStatus;
    } catch {
      return false;
    } finally {
      finishDetailRevalidation();
    }
  };

  const runAction = async <T,>(
    action: () => Promise<T>,
    done?: string | ((result: T) => FulfillmentToastState | undefined),
    /** How the calling form words a refusal it knows how to read; absent, the server's own sentence stands. */
    describeRefusal?: (message: string | string[] | undefined, body?: AdminActionErrorBody) => string | undefined,
    partialFailure?: (err: unknown) => string | undefined,
    /** Bulk transport failures have unknown item counts; ordinary saves retain the server refusal. */
    bulkResult = false,
    preferRefusal = false,
  ): Promise<boolean> => {
    if (inFlightRef.current) {
      return false;
    }
    inFlightRef.current = true;
    const targetOrderId = header.orderId;
    setBusy(true);
    let result: T;
    try {
      result = await action();
    } catch (err) {
      if (loadedOrderIdRef.current !== String(targetOrderId) || bulkResult) {
        if (loadedOrderIdRef.current === String(targetOrderId)) {
          showError(UNCONFIRMED_BULK_RESULT);
        }
        inFlightRef.current = false;
        setBusy(false);
        return false;
      }
      const partial = partialFailure?.(err);
      const failure = isAxiosError<AdminActionErrorBody>(err) ? err : undefined;
      // The order route answers through fetch, so its refusal body rides on the OMSRequestError (SSD-7450).
      const body = failure?.response?.data ?? omsRefusalBody(err);
      lastLineRefusalRef.current = lineEditRefusalsOf(body?.message);
      const blocked = failure?.response?.status === 409 ? body?.violations : undefined;
      if (partial) {
        showWarning(partial, [serverRefusal(err) ?? GENERIC_ACTION_FAILURE]);
      } else if (Array.isArray(blocked)) {
        showError('Action blocked', blocked);
      } else {
        lastAddressRefusalRef.current = addressRefusalsOf(
          body?.message,
          SHIPPING_ADDRESS_FIELDS.map((field) => field.key),
        );
        showError(
          (preferRefusal && !(body?.code === 'CONFLICT' && body?.changedBy)
            ? describeRefusal?.(body?.message, body)
            : describeStaleWrite(failure)) ??
            describeRefusal?.(body?.message, body) ??
            describeStaleWrite(failure) ??
            serverRefusal(err) ??
            GENERIC_ACTION_FAILURE,
        );
      }
      // A refusal on a row that already moved leaves the screen showing the version that was refused,
      // so it is re-read; so does a partial failure, whose first write already changed the order.
      // A gate refusal describes the row on screen and needs no reload [T03].
      if (partial || (body?.code && ORDER_CHANGING_REFUSALS.has(body.code))) {
        await refreshDetail();
      }
      inFlightRef.current = false;
      setBusy(false);
      return false;
    }
    if (loadedOrderIdRef.current !== String(targetOrderId)) {
      inFlightRef.current = false;
      setBusy(false);
      return true;
    }
    const unchanged = typeof result === 'object' && result !== null && 'changed' in result && result.changed === false;
    let refreshedSuccessfully = false;
    const csRevisionAtRead = csWriteRef.current.revision;
    try {
      const refreshed = await client.getOrderDetailForOutbound(targetOrderId);
      if (loadedOrderIdRef.current === String(targetOrderId)) {
        refreshedSuccessfully = true;
        applyDetailReadWithCsGuard(refreshed, csRevisionAtRead);
        setSelectedPic(refreshed.header.picUser ?? '');
        setReloadToken((token) => token + 1);
      }
    } catch {
      if (!unchanged && typeof done !== 'function' && loadedOrderIdRef.current === String(targetOrderId)) {
        showWarning(
          `${done && !unchanged ? done + ' ' : 'The action succeeded. '}Could not refresh the order; reload before taking another action.`,
        );
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
    if (typeof done === 'function' && loadedOrderIdRef.current === String(targetOrderId)) {
      const outcome = done(result);
      if (outcome) {
        if (!refreshedSuccessfully) {
          outcome.tone = outcome.tone === 'error' ? 'error' : 'warning';
          outcome.text += ' Could not refresh the order; reload before taking another action.';
        }
        const show = outcome.tone === 'error' ? showError : outcome.tone === 'warning' ? showWarning : showSuccess;
        show(outcome.text, outcome.details);
      }
    } else if (
      typeof done === 'string' &&
      !unchanged &&
      refreshedSuccessfully &&
      loadedOrderIdRef.current === String(targetOrderId)
    ) {
      showSuccess(done);
    }
    return true;
  };

  const csStatusNotice = (targetOrderId: number, csStatus: ManualCsStatus) =>
    `Set order ${targetOrderId} CS status to ${csStatus}? This changes only the CS display and audit. Stock, warehouse outbound, carrier orders, payments and WooCommerce are not changed.`;

  const writeCsStatus = async (
    targetOrderId: number,
    write: () => Promise<ChangeCsStatusResponse>,
    done: string | ((result: ChangeCsStatusResponse) => FulfillmentToastState | undefined),
    partialFailure?: (err: unknown) => string | undefined,
  ): Promise<void> => {
    await runAction(
      async () => {
        const result = await write();
        // Keep the confirmed server result if the subsequent reload fails; a newer reload still wins.
        if (loadedOrderIdRef.current === String(targetOrderId)) {
          csWriteRef.current = { orderId: targetOrderId, revision: csWriteRef.current.revision + 1 };
          setDetail((current) =>
            current?.header.orderId === targetOrderId
              ? {
                  ...current,
                  header: {
                    ...current.header,
                    csStatus: result.csStatus,
                    csResolutionMethod: result.csResolutionMethod ?? null,
                  },
                }
              : current,
          );
        }
        return result;
      },
      done,
      undefined,
      partialFailure,
    );
  };

  const markCsStatus = async (csStatus: 'delivered' | 'canceled') => {
    if (csActionRef.current || inFlightRef.current) {
      return;
    }
    csActionRef.current = true;
    setBusy(true);
    const targetOrderId = header.orderId;
    const onTarget = () => loadedOrderIdRef.current === String(targetOrderId);
    const label = csStatus === 'delivered' ? 'Mark delivered' : 'Mark canceled';
    const askDisplayOnly = () =>
      askConfirm({
        title: label,
        message: csStatusNotice(targetOrderId, csStatus),
        confirmLabel: label,
        cancelLabel: 'Keep current CS status',
      });
    try {
      let warehouse = false;
      let agreed: boolean;
      if (csStatus === 'canceled') {
        let eligibility;
        try {
          eligibility = await client.getPreOutboundCancelEligibility(targetOrderId);
        } catch (err) {
          if (onTarget()) {
            showError(
              serverRefusal(err) ??
                'Could not check warehouse cancellation eligibility. No changes were made. Try again.',
            );
          }
          return;
        }
        if (!onTarget()) {
          return;
        }
        if (eligibility.eligible) {
          const choice = await askBinaryChoice({
            title: label,
            message:
              'Also cancel warehouse work and release reservations? Standing lines will be canceled and active reservations released. Received stock and inbound history are kept. No WooCommerce update, refund or customer email is sent.',
            confirmLabel: 'Mark canceled and cancel warehouse work',
            secondaryLabel: 'Mark canceled only',
            cancelLabel: 'Keep current CS status',
          });
          agreed = choice !== null;
          warehouse = choice === 'primary';
        } else {
          agreed = await askDisplayOnly();
        }
      } else {
        agreed = await askDisplayOnly();
      }
      if (!agreed || !onTarget()) {
        return;
      }
      const apply = async (confirmWarehouse: boolean) => {
        // The action catch assigns this before runAction invokes partialFailure and refreshes the order.
        let ineligible = false;
        await writeCsStatus(
          targetOrderId,
          async () => {
            try {
              return confirmWarehouse
                ? await client.changeCsStatus(targetOrderId, csStatus, true)
                : await client.changeCsStatus(targetOrderId, csStatus);
            } catch (err) {
              const body = isAxiosError<AdminActionErrorBody>(err) ? err.response?.data : undefined;
              ineligible = confirmWarehouse && body?.code === 'PRE_OUTBOUND_CANCELLATION_INELIGIBLE';
              throw err;
            }
          },
          confirmWarehouse
            ? (result) =>
                result.warehouseCancellation
                  ? {
                      tone: 'success',
                      text: `Order ${targetOrderId} CS status marked ${csStatus}. Warehouse lines canceled: ${result.warehouseCancellation.cancelledLines}.`,
                    }
                  : {
                      tone: 'warning',
                      text: `Order ${targetOrderId} CS status marked ${csStatus}. The server did not confirm warehouse cancellation; check the order.`,
                    }
            : `Order ${targetOrderId} CS status marked ${csStatus}.`,
          () =>
            ineligible
              ? 'Warehouse cancellation was not possible. No CS or warehouse changes were made. You may mark canceled only (display-only).'
              : undefined,
        );
        return ineligible;
      };
      if ((await apply(warehouse)) && onTarget()) {
        // runAction released its write lock; reserve it again while the fallback decision is pending.
        inFlightRef.current = true;
        setBusy(true);
        const displayOnly = await askConfirm({
          title: 'Warehouse cancellation not possible',
          message:
            'The order is no longer eligible for warehouse cancellation. Mark canceled only? This changes only the CS display and audit; warehouse work and reservations are not changed.',
          confirmLabel: 'Mark canceled only',
          cancelLabel: 'Keep current CS status',
        });
        inFlightRef.current = false;
        if (displayOnly && onTarget()) {
          await apply(false);
        }
      }
    } finally {
      inFlightRef.current = false;
      csActionRef.current = false;
      setBusy(false);
    }
  };

  /** Resolved is recorded with how it was resolved; an order already resolved reopens here to change the method. */
  const markCsResolved = async () => {
    const targetOrderId = header.orderId;
    const choice = await askChoice({
      title: 'Mark resolved',
      message: csStatusNotice(targetOrderId, 'resolved'),
      label: 'Resolution method',
      options: CS_RESOLUTION_METHODS.map((method) => ({
        value: method.value,
        label: `${method.label} (${method.hint})`,
      })),
      ...(header.csStatus === 'resolved' && header.csResolutionMethod
        ? { defaultValue: header.csResolutionMethod }
        : {}),
      confirmLabel: 'Mark resolved',
      cancelLabel: 'Keep current CS status',
    });
    if (choice === null || !isCsResolutionMethod(choice) || loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    await writeCsStatus(
      targetOrderId,
      () => client.changeCsStatus(targetOrderId, 'resolved', choice),
      `Order ${targetOrderId} CS status marked resolved (${csResolutionMethodLabel(choice)}).`,
    );
  };

  /** The reason is asked for on the click: a form below the alerts can be scrolled out of view [BR-24]. */
  const submitHold = async () => {
    const reason = await ask({ title: 'Hold order', label: 'Reason', confirmLabel: 'Hold order' });
    if (reason === null) {
      return;
    }
    void runAction(() => client.holdOrder(header.orderId, { reason }), `Order ${header.orderId} is on hold`);
  };

  /** What the pencil starts from: the stored address, plus the two things only shipping carries. */
  const storedAddressDraft = (slot: AddressSlot): AddressDraft => {
    const shipping = readRecord(header.shipping);
    return {
      address: addressDraftOf(shipping[slot], addressFieldsFor(slot)),
      consignee: slot === 'shippingAddress' ? addressDraftOf(shipping['consignee'], CONSIGNEE_FIELDS) : {},
      shippingMessage: typeof shipping['shippingMessage'] === 'string' ? shipping['shippingMessage'] : '',
    };
  };

  const openAddressForm = (slot: AddressSlot) => {
    setAddressDraft(storedAddressDraft(slot));
    setEditingAddress(slot);
  };

  const submitAddress = async () => {
    if (!editingAddress) {
      return;
    }
    const stored = storedAddressDraft(editingAddress);
    const address = changedFieldsOf(addressDraft.address, stored.address);
    const consignee = changedFieldsOf(addressDraft.consignee, stored.consignee);

    const request: UpdateOrderAddressRequest = {};
    if (Object.keys(address).length > 0) {
      request[editingAddress] = address;
    }
    if (Object.keys(consignee).length > 0) {
      request.consignee = consignee;
    }
    if (addressDraft.shippingMessage !== stored.shippingMessage) {
      request.shippingMessage = addressDraft.shippingMessage;
    }

    setAddressRefusals({});
    try {
      const applied = await runAction(
        () => client.updateOrderAddress(header.orderId, request),
        editingAddress === 'shippingAddress' ? 'Shipping address saved.' : 'Billing address saved.',
      );
      if (applied) {
        setEditingAddress(null);
      }
    } finally {
      // runAction swallows the error to report it; the per-field half is read off the last failure.
      setAddressRefusals(lastAddressRefusalRef.current);
      lastAddressRefusalRef.current = {};
    }
  };

  /** Every rule the open draft breaks; the save waits while any of them stands [T16]. */
  const addressErrors = Object.entries(addressDraft.address)
    .map(([key, value]) => describeAddressField(key, value))
    .filter((message): message is string => message !== null);

  /** Saving an address that matches the stored one would write an empty patch, so the button waits. */
  const addressUnchanged =
    editingAddress != null &&
    Object.keys(changedFieldsOf(addressDraft.address, storedAddressDraft(editingAddress).address)).length === 0 &&
    Object.keys(changedFieldsOf(addressDraft.consignee, storedAddressDraft(editingAddress).consignee)).length === 0 &&
    addressDraft.shippingMessage === storedAddressDraft(editingAddress).shippingMessage;

  /** The actor log is a view over persisted events, never a second copy — only goods movements land here. */
  const actorLogRows = events
    .filter((event) => ACTOR_LOG_ACTION_CLASSES.has(event.operation))
    .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime())
    .map((event) => {
      const payload = payloadOf(event);
      const cancel = event.operation === 'ADMIN_INBOUND_CANCEL' ? summarizeInboundCancel(payload) : undefined;
      const qty = cancel ? cancel.qty : payload['qty'];
      const action = event.operation.replace(/_/g, ' ');
      return {
        id: event.id,
        at: kstTimestamp(event.occurredAt),
        action: cancel?.outcome ? `${action} (${cancel.outcome})` : action,
        actionClass: ACTOR_LOG_ACTION_CLASSES.get(event.operation),
        sku: typeof payload['sku'] === 'string' ? (payload['sku'] as string) : '—',
        qty: typeof qty === 'number' ? qty : '—',
        operator: event.actor ?? '—',
        note: operatorNoteOf(payload) ?? '—',
      };
    });

  /** Who set which line value and when (SSD-7664) — the same persisted events, folded under the actor log. */
  const editRows = editLogRows(events, lines);

  const selectableLineNos = lines.filter((line) => canInboundLine(line.lineStatus)).map((line) => line.lineNo);
  /** A refresh can inbound or cancel a line under the operator, so the selection is always narrowed to what is still selectable. */
  const activeSelection = selectedLineNos.filter((lineNo) => selectableLineNos.includes(lineNo));

  const toggleLine = (lineNo: number) =>
    setSelectedLineNos((current) =>
      current.includes(lineNo) ? current.filter((no) => no !== lineNo) : [...current, lineNo].sort((a, b) => a - b),
    );

  const toggleAllLines = () =>
    setSelectedLineNos(activeSelection.length === selectableLineNos.length ? [] : selectableLineNos);

  const bulkInboundSelected = async () => {
    const selected = [...selectedLineNos];
    const targetOrderId = header.orderId;
    const locationCode = await ask({
      title: `Inbound ${activeSelection.length} line(s)`,
      label: 'Location',
      confirmLabel: 'Inbound',
    });
    if (locationCode === null) {
      return;
    }
    if (loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    const requested = selected.filter((lineNo) => inboundableRef.current.includes(lineNo));
    const excluded = selected.filter((lineNo) => !requested.includes(lineNo));
    const report = (result: Parameters<typeof inboundBulkResult>[0]) => {
      const outcome = inboundBulkResult(result, requested, excluded);
      setSelectedLineNos((current) => current.filter((lineNo) => !outcome.applied.includes(lineNo)));
      return outcome.notification;
    };
    if (requested.length === 0) {
      const outcome = report({ inbounded: 0, fulfillmentStatus: header.fulfillmentStatus, skipped: [], failures: [] });
      showWarning(outcome.text, outcome.details);
      return;
    }
    await runAction(
      () => client.bulkInboundLines(targetOrderId, { lineNos: requested, locationCode }),
      report,
      undefined,
      undefined,
      true,
    );
  };

  const inboundLine = async (lineNo: number) => {
    const locationCode = await ask({ title: `Inbound line ${lineNo}`, label: 'Location', confirmLabel: 'Inbound' });
    if (locationCode === null) {
      return;
    }
    void runAction(
      () => client.inboundLine(header.orderId, lineNo, { locationCode }),
      `Line ${lineNo} received onto ${locationCode}`,
    );
  };

  /** What happens to the goods is the operator's call, and these are the three controls the counting desk offers. */
  const cancelInboundLine = (lineNo: number) => {
    const line = lines.find((candidate) => candidate.lineNo === lineNo);
    setCancelInboundTarget({ lineNo, sku: line?.sku ?? '', arrivedQty: line?.totalQuantity ?? 1 });
  };

  const confirmCancelInbound = (request: CancelInboundLineRequest) => {
    const target = cancelInboundTarget;
    if (!target) {
      return;
    }
    setCancelInboundTarget(null);
    void runAction(
      () => client.cancelInbound(header.orderId, target.lineNo, request),
      `Inbound cancelled on line ${target.lineNo}`,
    );
  };

  const openSplitLine = (lineNo: number) => {
    setSplitRefusal(undefined);
    setSplitTarget({ orderId: header.orderId, lineNo });
  };

  const closeSplitLine = () => {
    setSplitTarget(null);
    setSplitRefusal(undefined);
  };

  /**
   * The dialog stays open on a refusal and says it there; a refusal about a line that moved re-reads the order.
   * An answer that lands after the operator moved to another order touches nothing on the new one.
   */
  const confirmSplitLine = async (parts: SplitOrderLinePart[]) => {
    if (splitTarget === null) {
      return;
    }
    const { orderId: targetOrderId, lineNo } = splitTarget;
    const fingerprint = attemptFingerprint([targetOrderId, lineNo, parts]);
    let failure: unknown;
    setSplitRefusal(undefined);
    const applied = await runAction(
      async () => {
        try {
          return await splitKeysRef.current.run(fingerprint, (idempotencyKey) =>
            client.splitOrderLine(targetOrderId, lineNo, { parts, idempotencyKey }),
          );
        } catch (err) {
          failure = err;
          throw err;
        }
      },
      (result: SplitOrderLineResponse) => ({ tone: 'success', text: describeLineSplitResult(result) }),
      (message, body) => describeLineSplitRefusal(lineNo, body?.code, message),
    );
    if (loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    if (applied) {
      closeSplitLine();
      return;
    }
    if (failure === undefined) {
      return;
    }
    const code = isAxiosError<AdminActionErrorBody>(failure) ? failure.response?.data?.code : undefined;
    setSplitRefusal(
      describeLineSplitRefusal(
        lineNo,
        code,
        isAxiosError<AdminActionErrorBody>(failure) ? failure.response?.data?.message : undefined,
      ) ??
        serverRefusal(failure) ??
        GENERIC_ACTION_FAILURE,
    );
    if (code && LINE_SPLIT_STALE_REFUSALS.has(code)) {
      await refreshDetail();
    }
  };

  const openLineToJit = (lineNo: number) => {
    setToJitRefusal(undefined);
    setToJitTarget({ orderId: header.orderId, lineNo });
  };

  const closeLineToJit = () => {
    setToJitTarget(null);
    setToJitRefusal(undefined);
  };

  /**
   * "No stock → awaiting purchase" (SSD-7506). The switch is sent against the line as this screen read it, so a line
   * that moved since is refused rather than switched blind; the dialog stays open on a refusal and says it there.
   */
  const confirmLineToJit = async (ask: SwitchLineToJitAsk) => {
    if (toJitTarget === null) {
      return;
    }
    const { orderId: targetOrderId, lineNo } = toJitTarget;
    const expectedUpdatedAt = lines.find((candidate) => candidate.lineNo === lineNo)?.updatedAt;
    if (!expectedUpdatedAt) {
      setToJitRefusal('This line does not say when it was last changed; reload the order first.');
      return;
    }
    const fingerprint = attemptFingerprint([targetOrderId, lineNo, ask.quantity, ask.reason, expectedUpdatedAt]);
    let failure: unknown;
    setToJitRefusal(undefined);
    const applied = await runAction(
      async () => {
        try {
          return await toJitKeysRef.current.run(fingerprint, (idempotencyKey) =>
            client.switchOrderLineToJit(targetOrderId, lineNo, { ...ask, expectedUpdatedAt, idempotencyKey }),
          );
        } catch (err) {
          failure = err;
          throw err;
        }
      },
      (result: SwitchOrderLineToJitResponse) => ({ tone: 'success', text: describeLineToJitResult(result) }),
      (message, body) => describeLineToJitRefusal(body?.code, message),
    );
    if (loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    if (applied) {
      closeLineToJit();
      return;
    }
    if (failure === undefined) {
      return;
    }
    const failureBody = isAxiosError<AdminActionErrorBody>(failure) ? failure.response?.data : undefined;
    setToJitRefusal(
      describeStaleWrite(isAxiosError<AdminActionErrorBody>(failure) ? failure : undefined) ??
        describeLineToJitRefusal(failureBody?.code, failureBody?.message) ??
        serverRefusal(failure) ??
        GENERIC_ACTION_FAILURE,
    );
    if (failureBody?.code && LINE_TO_JIT_STALE_REFUSALS.has(failureBody.code)) {
      await refreshDetail();
    }
  };

  const confirmExistingStock = async (ask: UseExistingStockAsk) => {
    if (!existingStockTarget || existingStockTarget.orderId !== header.orderId) {
      return;
    }
    const { orderId: targetOrderId, line } = existingStockTarget;
    const current = lines.find((candidate) => candidate.lineNo === line.lineNo);
    if (
      !line.updatedAt ||
      !current ||
      current.updatedAt !== line.updatedAt ||
      !offersLineToExistingInventory(current, detail)
    ) {
      return;
    }
    const fingerprint = attemptFingerprint([targetOrderId, line.lineNo, ask.locationCode, ask.reason, line.updatedAt]);
    const expectedUpdatedAt = line.updatedAt;
    let failure: unknown;
    setExistingStockRefusal(undefined);
    const applied = await runAction(
      async () => {
        try {
          return await existingStockKeysRef.current.run(fingerprint, (idempotencyKey) =>
            client.switchOrderLineToExistingInventory(targetOrderId, line.lineNo, {
              ...ask,
              expectedUpdatedAt,
              idempotencyKey,
            }),
          );
        } catch (err) {
          failure = err;
          throw err;
        }
      },
      `Line ${line.lineNo} now uses ${line.totalQuantity} unit(s) of existing stock at ${ask.locationCode}.`,
      (_message, body) => describeLineToExistingInventoryRefusal(body?.code),
    );
    if (loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    if (applied) {
      setExistingStockTarget(null);
      return;
    }
    if (failure === undefined) {
      return;
    }
    const body = isAxiosError<AdminActionErrorBody>(failure) ? failure.response?.data : undefined;
    setExistingStockRefusal(
      describeLineToExistingInventoryRefusal(body?.code) ?? serverRefusal(failure) ?? GENERIC_ACTION_FAILURE,
    );
    if (body?.code && !ORDER_CHANGING_REFUSALS.has(body.code)) {
      await refreshDetail();
    }
  };

  const cancelLine = async (lineNo: number) => {
    const line = lines.find((item) => item.lineNo === lineNo);
    const request = await askCancellation(
      `Cancel line ${lineNo}`,
      'Cancel line',
      line !== undefined && hasWritableReceivedGoods(line),
      64,
    );
    if (request === null) {
      return;
    }
    void runAction(() => client.cancelLine(header.orderId, lineNo, request), `Line ${lineNo} cancelled`);
  };

  const addLine = async (sku: string, quantity: number): Promise<boolean> => {
    if (!sku || !Number.isInteger(quantity) || quantity <= 0) {
      return false;
    }
    const fingerprint = JSON.stringify({ sku, qtyOrdered: quantity });
    if (pendingAddLineRef.current?.fingerprint !== fingerprint) {
      pendingAddLineRef.current = { fingerprint, idempotencyKey: uuidv4() };
    }
    const applied = await runAction(
      () =>
        client.addOrderLine(header.orderId, {
          sku,
          qtyOrdered: quantity,
          idempotencyKey: pendingAddLineRef.current!.idempotencyKey,
        }),
      `Line item added — ${sku}.`,
    );
    if (applied) {
      pendingAddLineRef.current = null;
    }
    return applied;
  };

  const openRowEdit = (line: FulfillmentOrderLine) => {
    if (describeLineEditBlock(line.lineStatus) !== '') {
      return;
    }
    // No parcel opens ready for a new waybill and one parcel opens on it, so the box needs no second click (SSD-7814).
    const opening = openingParcelOf(line.lineTrackingCouriers);
    const draft = { ...lineDraftOf(line), lineTrackingNumber: '', ...opening.fields };
    setLineDraft(draft);
    setLineEditBase({
      draft,
      updatedAt: line.updatedAt,
      parcels: line.lineTrackingCouriers,
      targetId: opening.targetId,
      ...(opening.adding && { adding: true, preWaybill: true }),
    });
    setLineRefusals({});
    setEditingLineNo(line.lineNo);
  };

  const selectCourierParcel = (id: string, remove = false) => {
    if (!lineEditBase) {
      return;
    }
    if (
      lineDraft.lineTrackingNumber !== lineEditBase.draft.lineTrackingNumber ||
      lineDraft.lineDeliveryCompany !== lineEditBase.draft.lineDeliveryCompany
    ) {
      showWarning('Save or cancel the current parcel changes before choosing another parcel.', undefined, AUTO_DISMISS);
      return;
    }
    const parcel = lineEditBase.parcels?.find((entry) => entry.trackingId === id);
    if (id && !parcel) {
      return;
    }
    const fields = { lineTrackingNumber: parcel?.number ?? '', lineDeliveryCompany: parcel?.carrier ?? '' };
    setLineDraft((draft) => ({ ...draft, ...fields, ...(remove ? { lineTrackingNumber: '' } : {}) }));
    setLineEditBase((base) =>
      base
        ? { ...base, draft: { ...base.draft, ...fields }, targetId: id || undefined, adding: !id, preWaybill: false }
        : base,
    );
    setLineRefusals((refusals) => {
      const next = { ...refusals };
      delete next.lineTrackingNumber;
      delete next.lineDeliveryCompany;
      return next;
    });
  };

  const closeRowEdit = () => {
    setEditingLineNo(null);
    setLineRefusals({});
  };

  /** A retyped field drops the server's verdict on it: that verdict was about the old value. */
  const editLineField = (field: LineEditField, value: string) => {
    setLineRefusals((refusals) => {
      const next = { ...refusals };
      delete next[field];
      return next;
    });
    setLineDraft((draft) => ({ ...draft, [field]: value }));
  };

  /**
   * One write of the open row. A refused field is said in this screen's words, on the toast and beside
   * the field: the validator's own sentence spells the wire name ("purchaseOrderDate must be a valid ISO 8601…").
   */
  const runLineEdit = async (
    action: () => Promise<unknown>,
    done?: string,
    partialFailure?: (err: unknown) => string | undefined,
    preserveServerSentence = false,
  ): Promise<boolean> => {
    lastLineRefusalRef.current = {};
    const describeRefusal = (message: string | string[] | undefined, body?: AdminActionErrorBody) => {
      // The platform clashes with the seller type already recorded, so the platform box is the one to change.
      if (body?.rule === PLATFORM_MISMATCH_RULE) {
        lastLineRefusalRef.current = { ...lastLineRefusalRef.current, purchasePlatform: PLATFORM_MISMATCH_MESSAGE };
        return describeSourcingRefusal(body);
      }
      if (preserveServerSentence) {
        return Array.isArray(message) ? message.join(' · ') : message;
      }
      return describeLineEditRefusal(message);
    };
    const applied = await runAction(action, done, describeRefusal, partialFailure, false, preserveServerSentence);
    // runAction swallows the error to report it; the per-field half is read off the last failure.
    setLineRefusals(lastLineRefusalRef.current);
    lastLineRefusalRef.current = {};
    return applied;
  };

  /** Only the retyped fields travel; an untouched one must not overwrite what the agent recorded. */
  const saveRowEdits = async (line: FulfillmentOrderLine) => {
    if (revalidationCountRef.current > 0 || describeLineEditBlock(line.lineStatus) !== '') {
      return;
    }
    // Non-courier fields keep SSD-7665's last-save-wins behavior: only retyped fields travel, so the rest remain
    // stored. Courier and waybill writes are version-guarded below because they address a specific inbound parcel.
    const stored = lineEditBase?.draft ?? lineDraftOf(line);
    const openedVersion = lineEditBase ? lineEditBase.updatedAt : line.updatedAt;
    // Validate the whole draft before either of this non-atomic pair of writes.
    if (Object.keys(editableLineErrorsOf(line, lineDraft, stored)).length > 0) {
      return;
    }
    const quantityChanged =
      !isWooCommerceOrder(header.orderId) &&
      describeLineQuantityBlock(line.lineStatus) === '' &&
      lineDraft.totalQuantity !== stored.totalQuantity;
    const totalQuantity = Number(lineDraft.totalQuantity);
    const request: UpdateLineAgentTrackingRequest = {};
    /** The unrecognized-pool parcels the saved numbers closed, said once the save lands (SSD-7442). */
    let poolMatched: UnrecognizedAutoMatch | undefined;
    // An emptied size goes back to the one the line was taken in with; the product itself never changes (SSD-7514).
    if (lineDraft.size !== stored.size) {
      request.size = lineDraft.size.trim() || null;
    }
    if (lineDraft.purchaseOrderNumber !== stored.purchaseOrderNumber) {
      request.purchaseOrderNumber = lineDraft.purchaseOrderNumber || null;
    }
    // Compared as the day the date input holds, so an instant nobody touched is not sent back as a change.
    if (lineDraft.purchaseOrderDate !== stored.purchaseOrderDate) {
      request.purchaseOrderDate = lineDraft.purchaseOrderDate || null;
    }
    if (lineDraft.purchasePlatform !== stored.purchasePlatform) {
      request.purchasePlatform = (lineDraft.purchasePlatform as SourcingPlatform) || null;
    }
    if (lineDraft.productCost !== stored.productCost) {
      const cost = lineDraft.productCost.trim();
      request.productCost = cost === '' ? null : Number(cost);
    }
    if (lineDraft.lineTrackingNumber !== stored.lineTrackingNumber) {
      const trackingNumber = lineDraft.lineTrackingNumber.trim();
      if (lineEditBase?.targetId || trackingNumber !== '') {
        request.lineTrackingNumber = trackingNumber === '' ? null : lineDraft.lineTrackingNumber;
      }
    }
    if (
      lineDraft.lineDeliveryCompany !== stored.lineDeliveryCompany &&
      !(lineEditBase?.adding && !lineEditBase.preWaybill && lineDraft.lineTrackingNumber.trim() === '')
    ) {
      request.lineDeliveryCompany = lineDraft.lineDeliveryCompany || null;
    }
    if (lineEditBase?.adding && request.lineTrackingNumber) {
      request.lineDeliveryCompany = lineDraft.lineDeliveryCompany || null;
    }
    const courierChanged = request.lineTrackingNumber !== undefined || request.lineDeliveryCompany !== undefined;
    if (courierChanged) {
      if (!openedVersion) {
        showWarning('Refresh the order before editing its waybill or delivery company.');
        return;
      }
      if (
        line.updatedAt !== openedVersion ||
        (lineEditBase?.parcels !== undefined &&
          JSON.stringify(line.lineTrackingCouriers) !== JSON.stringify(lineEditBase.parcels))
      ) {
        showWarning(`Line ${line.lineNo} changed since you opened it. Reopen Edit line to see the current values.`);
        return;
      }
      if (lineEditBase?.targetId) {
        request.lineTrackingId = lineEditBase.targetId;
        request.expectedTrackingNumber = stored.lineTrackingNumber;
      }
      request.expectedUpdatedAt = openedVersion;
    }

    if (request.lineTrackingNumber === null && lineEditBase?.targetId) {
      const agreed = await askConfirm({
        title: 'Delete waybill',
        message: `Remove waybill ${stored.lineTrackingNumber} from line ${line.lineNo}? Its original entry remains in history.`,
        confirmLabel: 'Delete waybill',
      });
      if (!agreed || loadedOrderIdRef.current !== String(header.orderId)) {
        return;
      }
    }

    const metadataChanged = Object.keys(request).length > 0;
    if (quantityChanged || metadataChanged) {
      let quantitySaved = false;
      const applied = await runLineEdit(
        async () => {
          if (quantityChanged) {
            const quantityResult = await client.changeOrderLineQuantity(header.orderId, line.lineNo, { totalQuantity });
            quantitySaved = quantityResult.changed !== false;
            if (courierChanged) {
              // The first write changes the version. Keep the second write's stale-row protection.
              const refreshed = await client.getOrderDetailForOutbound(header.orderId);
              const updatedLine = refreshed.lines.find((entry) => entry.lineNo === line.lineNo);
              if (!updatedLine?.updatedAt) {
                throw new Error('Could not confirm the updated line version.');
              }
              // The quantity write is ours, but a gig refresh or another courier edit between writes is not.
              if (
                (request.lineTrackingNumber !== undefined || request.lineDeliveryCompany !== undefined) &&
                ((lineEditBase?.parcels !== undefined &&
                  JSON.stringify(updatedLine.lineTrackingCouriers) !== JSON.stringify(lineEditBase.parcels)) ||
                  (!lineEditBase?.targetId &&
                    !lineEditBase?.adding &&
                    (updatedLine.lineDeliveryCompany ?? '') !== stored.lineDeliveryCompany))
              ) {
                throw new Error(
                  'The inbound parcel changed while saving quantity. Reopen Edit line before changing its delivery company or waybill.',
                );
              }
              request.expectedUpdatedAt = updatedLine.updatedAt;
              // The saved quantity is now part of the row the editor holds, so a retry after a refused
              // metadata write neither resends it nor mistakes this editor's own write for someone else's.
              setLineEditBase({
                ...lineEditBase,
                draft: { ...stored, totalQuantity: lineDraft.totalQuantity },
                updatedAt: updatedLine.updatedAt,
              });
            } else if (metadataChanged) {
              setLineEditBase((base) =>
                base ? { ...base, draft: { ...stored, totalQuantity: lineDraft.totalQuantity } } : base,
              );
            }
          }
          if (metadataChanged) {
            poolMatched = (await client.updateLineAgentTracking(header.orderId, line.lineNo, request))
              .unrecognizedMatched;
          }
          return { changed: quantitySaved || metadataChanged };
        },
        `Line ${line.lineNo} saved.`,
        () => (quantitySaved ? `Line ${line.lineNo} quantity saved; other changes were not saved.` : undefined),
        courierChanged,
      );
      if (!applied) {
        return;
      }
      if (poolMatched && poolMatched.rows > 0) {
        // The box was waiting in the pool under this number. Its Slack thread gets a reply too, but that post can fail
        // without failing the match, so the toast says only what the save did and where the box waits.
        showSuccess(
          `Also matched ${poolMatched.rows} unrecognized parcel${poolMatched.rows === 1 ? '' : 's'} to this line${
            poolMatched.locations.length > 0 ? `; the box waits at ${poolMatched.locations.join(', ')}` : ''
          }.`,
        );
      }
    }
    closeRowEdit();
  };

  /** Undoing a shipment moves stock back; every other destructive action here asks first, so this does [T16]. */
  const cancelOutboundOrder = async () => {
    const agreed = await askConfirm({
      title: 'Cancel Outbound',
      message: `Order ${header.orderId} goes back to READY and its parcel stops counting as shipped.`,
      confirmLabel: 'Cancel outbound',
    });
    if (!agreed) {
      return;
    }
    void runAction(
      () => client.cancelOutbound(header.orderId),
      `Outbound cancelled — order ${header.orderId} is back to READY`,
    );
  };

  const assignPic = async () => {
    if (!selectedPic || selectedPic === header.picUser) {
      return;
    }
    const applied = await runAction(
      () => client.assignOrderPic(header.orderId, { picUser: selectedPic }),
      `PIC assigned to ${selectedPic}.`,
    );
    if (applied) {
      setEditingPic(false);
    }
  };

  /** Saves the Shelf through the same write View Orders uses; an empty box clears it. */
  const saveShelf = async () => {
    const orderId = header.orderId;
    const value = shelf.trim();
    // Inbound books the saved Shelf, so what is saved is the spelling the server will take [SSD-7817].
    const code = value ? readShelfCode(value) : '';
    // Only a shelf code is kept in the Shelf, the rule View Orders applies [SSD-7226 D2].
    if (code === null) {
      showError(`${value} is not a shelf code. ${SHELF_CODE_RULE}`, undefined, AUTO_DISMISS);
      return;
    }
    const typing = shelfTypingRef.current;
    const boxStillSaved = () => shelfTypingRef.current === typing && loadedOrderIdRef.current === String(orderId);
    const applied = await runAction(async () => {
      await client.updateTemporaryLocation(orderId, { value: code || null });
      // Saved, so the reload that follows shows the stored Shelf rather than keeping the typing.
      if (boxStillSaved()) {
        shelfEditRef.current = null;
      }
    });
    if (applied && boxStillSaved()) {
      setShelf(code);
      setShelfDirty(false);
      setShelfSaved(true);
    }
  };

  const clearPic = async () => {
    if (!header.picUser) {
      return;
    }
    const agreed = await askConfirm({
      title: 'Clear PIC',
      message: `${header.picUser} stops being the person in charge of this order.`,
      confirmLabel: 'Clear PIC',
    });
    if (!agreed) {
      return;
    }
    const applied = await runAction(() => client.assignOrderPic(header.orderId, { picUser: null }), 'PIC cleared.');
    if (applied) {
      setEditingPic(false);
    }
  };

  const correctTracking = async () => {
    if (!trackingNumber.trim() || !trackingCarrier.trim()) {
      return;
    }
    const applied = await runAction(
      async () => {
        const result = await client.correctOrderTracking(header.orderId, {
          number: trackingNumber.trim(),
          carrier: trackingCarrier.trim(),
        });
        // This persisted fact does not depend on readback; a no-op must leave it untouched.
        if (result.changed && loadedOrderIdRef.current === String(header.orderId)) {
          setTrackingInUse(result.warning ?? null);
        }
        return result;
      },
      (result) => {
        if (!result.changed) {
          return;
        }
        // Reusing a number can be deliberate; warn only when a change was actually saved.
        return result.warning
          ? {
              tone: 'warning',
              text: `Tracking saved — order ${result.warning.orderId} is travelling under the same waybill.`,
            }
          : { tone: 'success', text: 'Tracking saved.' };
      },
    );
    if (applied) {
      setTrackingNumber('');
      setTrackingCarrier('');
      setEditingTracking(false);
    }
  };

  /**
   * Opens on empty boxes and drops what was typed on the way out: a value left behind from an earlier
   * attempt came back on reopening, one Save away from landing on this order.
   */
  const toggleTrackingEditor = () => {
    setTrackingNumber('');
    setTrackingCarrier('');
    setEditingTracking((editing) => !editing);
  };

  /** Correcting a waybill to an empty string is refused, so taking it off needs a route of its own [T16]. */
  const clearTracking = async () => {
    const agreed = await askConfirm({
      title: 'Clear tracking',
      message: `Order ${header.orderId} loses its waybill. Nothing is shipped or unshipped by this — the parcel simply stops being named.`,
      confirmLabel: 'Clear tracking',
    });
    if (!agreed) {
      return;
    }
    setTrackingInUse(null);
    void runAction(() => client.clearOrderTracking(header.orderId), 'Tracking cleared.');
  };

  const submitResolve = async () => {
    const reason = await ask({ title: 'Resolve order', label: 'Reason', confirmLabel: 'Resolve' });
    if (reason === null) {
      return;
    }
    void runAction(
      () => client.resolveOrder(header.orderId, { reason, operator: operatorName() }),
      `Order ${header.orderId} resolved.`,
    );
  };

  /**
   * The Order team's "Closed" [SSD-7056]: ends an order that never shipped by cancelling its open lines and
   * releasing the stock they hold. V2 only — WooCommerce keeps its status and the customer gets no email.
   */
  const submitCloseUnshipped = async () => {
    const targetOrderId = header.orderId;
    const reason = await ask({ title: 'Close order', label: 'Reason', confirmLabel: 'Next', maxLength: 200 });
    if (reason === null || loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    const agreed = await askConfirm({
      title: 'Close order without shipping',
      message:
        `Order ${targetOrderId} is closed without shipping: every open line is cancelled and the stock it holds ` +
        `is released. WooCommerce is not changed and the customer gets no email. Reason: ${reason}`,
      confirmLabel: 'Close order',
    });
    if (!agreed || loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    // Either 409 means the order moved since the menu was drawn, so the screen is re-read after the refusal.
    const outcome = { stale: false };
    await runAction(
      async () => {
        try {
          return await client.closeUnshippedOrder(targetOrderId, { reason, operator: operatorName() });
        } catch (err) {
          outcome.stale =
            isAxiosError(err) &&
            err.response?.status === 409 &&
            !ORDER_CHANGING_REFUSALS.has((err.response.data as AdminActionErrorBody | undefined)?.code ?? '');
          throw err;
        }
      },
      `Order ${targetOrderId} closed.`,
      (_message, body) => describeCloseUnshippedRefusal(body),
    );
    if (outcome.stale) {
      await refreshDetail();
    }
  };

  const submitUnresolve = async () => {
    const reason = await ask({ title: 'Lift the RESOLVED verdict', label: 'Reason', confirmLabel: 'Lift verdict' });
    if (reason === null) {
      return;
    }
    void runAction(
      () => client.unresolveOrder(header.orderId, { reason, operator: operatorName() }),
      `Resolved verdict lifted — order ${header.orderId}.`,
    );
  };

  const addComment = async () => {
    if (!commentText.trim() || inFlightRef.current) {
      return;
    }
    const text = commentText.trim();
    const pending =
      pendingCommentRef.current?.text === text ? pendingCommentRef.current : { text, idempotencyKey: uuidv4() };
    pendingCommentRef.current = pending;
    const targetOrderId = header.orderId;
    inFlightRef.current = true;
    setBusy(true);
    try {
      const result = await client.appendOrderComment(targetOrderId, pending);
      if (loadedOrderIdRef.current !== String(targetOrderId)) {
        return;
      }
      showSuccess('Comment added.');
      setComments(result.comments);
      setCommentText('');
      pendingCommentRef.current = null;
    } catch {
      if (loadedOrderIdRef.current === String(targetOrderId)) {
        showError('Could not add the comment.');
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  /** Keeps what is typed in one line's Memo box. */
  const setMemoDraft = (lineNo: number, text: string) => {
    setMemoDrafts((drafts) => ({ ...drafts, [lineNo]: text }));
  };

  /** A memo about one line goes on the same thread with the line's number, so View Orders reads it under that line (SSD-7265). */
  const addLineMemo = async (lineNo: number) => {
    const text = (memoDrafts[lineNo] ?? '').trim();
    if (!text || inFlightRef.current) {
      return;
    }
    const targetOrderId = header.orderId;
    inFlightRef.current = true;
    setBusy(true);
    try {
      const result = await memoKeysRef.current.run(
        attemptFingerprint([targetOrderId, lineNo, text]),
        (idempotencyKey) => client.appendOrderComment(targetOrderId, { text, idempotencyKey, lineNo }),
      );
      if (loadedOrderIdRef.current !== String(targetOrderId)) {
        return;
      }
      showSuccess(`Memo added to line ${lineNo}.`);
      setComments(result.comments);
      setMemoDrafts((drafts) => {
        const rest = { ...drafts };
        delete rest[lineNo];
        return rest;
      });
    } catch {
      if (loadedOrderIdRef.current === String(targetOrderId)) {
        showError('Could not add the memo.');
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  /** Takes one comment off the order, or one memo off a line, once the desk says Yes; the server keeps what it said in the order's history (SSD-7265). */
  const deleteComment = async (comment: OrderComment, lineNo?: number) => {
    const shown = comment.text.length > 80 ? `${comment.text.slice(0, 80)}…` : comment.text;
    const onLine = lineNo !== undefined;
    const agreed = await askConfirm({
      title: onLine ? 'Delete memo' : 'Delete comment',
      message: onLine
        ? `Are you sure you want to delete this memo on line ${lineNo}? "${shown}"`
        : `Are you sure you want to delete this comment? "${shown}"`,
      confirmLabel: 'Yes',
      cancelLabel: 'No',
    });
    if (!agreed || inFlightRef.current) {
      return;
    }
    const targetOrderId = header.orderId;
    inFlightRef.current = true;
    setBusy(true);
    try {
      const result = await client.removeOrderComment(targetOrderId, comment.at);
      if (loadedOrderIdRef.current !== String(targetOrderId)) {
        return;
      }
      showSuccess(onLine ? `Memo deleted from line ${lineNo}.` : 'Comment deleted.');
      setComments(result.comments);
    } catch {
      if (loadedOrderIdRef.current === String(targetOrderId)) {
        showError(onLine ? 'Could not delete the memo.' : 'Could not delete the comment.');
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const operatorName = (): string => user?.name || user?.email || 'unknown-user';

  /** Routes the chosen fulfillment status to the action that owns it, never to a bare status write. */
  /** Reason a choice cannot be taken, empty when it can — the menu says it rather than hiding the row [BR-46]. */
  const statusChoiceBlock = (choice: FulfillmentStatusChoice): string => {
    // On 2.0 READY on a READY web order is how an order 2.0 made READY by itself is announced: the server
    // pushes prepare-shipment and sends the Preparing Order email, once per order (SSD-7187).
    if (
      launch === 'on' &&
      notInWooCommerce === '' &&
      choice.status === 'READY' &&
      header.fulfillmentStatus === 'READY'
    ) {
      return '';
    }
    // Re-picking the status the order already holds fired the action again and earned a 409 [T16].
    // Lifting a verdict is the exception: it is the one move out of the status the order is in.
    if (choice.status === header.fulfillmentStatus && choice.internal !== 'unresolve') {
      return `This order is already ${fulfillmentStatusLabel(choice.status, detail.resolutionKind)}`;
    }
    // A RESOLVED order is rewritten back to RESOLVED by every recalc, so any other choice would land as
    // a silent no-op: the store status moves and the fulfillment status does not [FS-016].
    if (isClosedUnshipped) {
      return `This order is ${CLOSED_UNSHIPPED_LABEL} and cannot be moved`;
    }
    if (isResolved && choice.internal !== 'unresolve') {
      return 'This order reads RESOLVED — lift the verdict before moving it';
    }
    // INBOUNDED is no fulfillment status, so the real condition is whether a line is still waiting.
    if (choice.action === 'inbound') {
      return lines.some((line) => canInboundLine(line.lineStatus))
        ? ''
        : 'No line on this order is waiting to be inbounded';
    }
    // Lifting a verdict is not a transition: the table calls RESOLVED terminal, which is the state this
    // entry exists to leave. Gating it on the table made the only way out of RESOLVED unreachable.
    if (choice.internal === 'unresolve') {
      return '';
    }
    // The server's own transition table, so menu and guard cannot drift; an absent one leaves the older gates [T16].
    if (allowedTransitions && !allowedTransitions.includes(choice.status)) {
      return `An order that is ${fulfillmentStatusLabel(header.fulfillmentStatus)} cannot become ${fulfillmentStatusLabel(choice.status)}`;
    }
    if (choice.action === 'outbound') {
      return carrierOutboundBlock;
    }
    if (choice.action || choice.internal) {
      return '';
    }
    // On 2.0 CANCELLED is the Cancel Order flow, so it is offered exactly when that button is — on an order
    // WooCommerce never saw too, where that flow cancels in fulfillment.
    if (launch === 'on' && choice.status === 'CANCELLED') {
      return cancelOrderBlock;
    }
    if (notInWooCommerce !== '') {
      return `${notInWooCommerce}, and only WooCommerce sets this status`;
    }
    // Until the switch is read the write could land on V1's status route, which answers 410 once 2.0 has launched.
    if (launch === 'unknown') {
      return 'Checking whether Fulfillment 2.0 has launched';
    }
    // No store status maps to READY any more (SSD-7450), so before launch nothing in the menu can set it.
    if (launch !== 'on' && !choice.wcStatus) {
      return `Only Fulfillment 2.0 sets ${fulfillmentStatusLabel(choice.status)}: no WooCommerce status maps to it`;
    }
    return '';
  };

  const applyStatusChoice = async (choice: FulfillmentStatusChoice) => {
    if (choice.action) {
      await changeFulfillmentStatus(choice.action === 'outbound' ? 'OUTBOUNDED' : 'INBOUNDED');
      return;
    }
    if (choice.internal === 'resolve') {
      await submitResolve();
      return;
    }
    if (choice.internal === 'unresolve') {
      await submitUnresolve();
      return;
    }
    // On 2.0 V1's status route answers 410, so these go through the V2 status route or the cancel flow (SSD-7187).
    if (launch === 'on' && choice.status === 'CANCELLED') {
      await startCancelOrder();
      return;
    }
    if (launch === 'on' && isFulfillmentMenuStatus(choice.status)) {
      await changeFulfillmentMenuStatus(choice.status);
      return;
    }
    // READY has no store status behind it any more (SSD-7450); the menu holds it before launch, so nothing to send.
    if (choice.wcStatus) {
      await changeWooCommerceStatus(choice.wcStatus);
    }
  };

  /**
   * The menu's store statuses on 2.0: the server moves the lines and tells WooCommerce and the customer itself.
   * A 409 means the order moved since the menu was drawn, so the screen is re-read after the refusal.
   */
  const changeFulfillmentMenuStatus = async (status: FulfillmentMenuStatus) => {
    const targetOrderId = header.orderId;
    const outcome = { stale: false };
    await runAction(
      async () => {
        try {
          return await client.changeOrderStatus(targetOrderId, status, operatorName());
        } catch (err) {
          outcome.stale =
            isAxiosError(err) &&
            err.response?.status === 409 &&
            !ORDER_CHANGING_REFUSALS.has((err.response.data as AdminActionErrorBody | undefined)?.code ?? '');
          throw err;
        }
      },
      `Order ${targetOrderId} set to ${fulfillmentStatusLabel(status)}.`,
    );
    if (outcome.stale && loadedOrderIdRef.current === String(targetOrderId)) {
      await refreshDetail();
    }
  };

  /** The same cues as bulk outbound, played on the server's answer rather than on the click. */
  const outboundOrder = () =>
    runAction(async () => {
      try {
        const block = outboundCarrierBlock(header.orderId, tracking, pendingParcels);
        if (block) {
          throw new OutboundCarrierGateError(block);
        }
        const result = await client.outboundOrder(header.orderId);
        FulfillmentSound.success();
        return result;
      } catch (err) {
        FulfillmentSound.failure();
        throw err;
      }
    }, `Outbound complete — order ${header.orderId}`);

  const changeFulfillmentStatus = async (status: 'INBOUNDED' | 'OUTBOUNDED') => {
    if (status === 'OUTBOUNDED') {
      await outboundOrder();
      return;
    }
    const targetOrderId = header.orderId;
    const locationCode = await ask({ title: 'Inbound every open line', label: 'Location', confirmLabel: 'Inbound' });
    if (locationCode === null || loadedOrderIdRef.current !== String(targetOrderId)) {
      return;
    }
    const requested = [...allInboundLineNosRef.current];
    await runAction(
      () => client.bulkInboundLines(header.orderId, { all: true, locationCode }),
      (result) => inboundBulkResult(result, requested).notification,
      undefined,
      undefined,
      true,
    );
  };

  const openWooCommerceAdmin = () => {
    const wpUrl = (getConfig().wpAdminUrl ?? '').replace(/\/$/, '');
    window.open(`${wpUrl}/admin.php?page=wc-orders&action=edit&id=${header.orderId}`, '_blank', 'noopener,noreferrer');
  };

  /** The trail is still the OMS one — read here in a modal so answering a question costs nobody their place. */
  const openAuditHistory = () => {
    setAuditOpen(true);
  };

  /** The label and the PACKING sheet behind it, told as one: the first refusal names its printer; a missing sheet is named too. */
  const reportParcelPrint = (result: ParcelPrintOutcome, done: string) => {
    if (loadedOrderIdRef.current !== String(header.orderId)) {
      return;
    }
    const refused = sheetOutcomesOf([result]).find((outcome): outcome is PrintFailure => !outcome.ok);
    if (refused) {
      showError(PrintFailureNotice.textOf(refused));
      return;
    }
    if (result.label.ok) {
      showSuccess(
        result.sheetSent
          ? done
          : `${done.replace(/\.$/, '')} — PACKING sheet not printed (packing invoice unavailable); print it from Packing Invoice.`,
      );
    }
  };

  /**
   * The OMS answers a refused change with null, but it has been seen to move WooCommerce before failing on an
   * order it cannot find, so a refusal means unknown rather than unchanged: reload either way and say so.
   */
  const changeWooCommerceStatus = async (status: string) => {
    if (inFlightRef.current) {
      return;
    }
    const targetOrderId = header.orderId;
    inFlightRef.current = true;
    setBusy(true);
    let updated: unknown = null;
    try {
      updated = await oms.updateOrderStatus(String(header.orderId), status, operatorName());
    } catch {
      updated = null;
    }
    try {
      const reloaded = await refreshDetail(updated ? status : undefined);
      if (loadedOrderIdRef.current !== String(targetOrderId)) {
        return;
      }
      if (!reloaded) {
        showError(
          'Could not confirm the status change and could not reload the order. Reload the page before acting again.',
        );
      } else if (!updated) {
        showError('Could not confirm the WooCommerce status change — the order was reloaded, check the value below.');
      } else {
        showSuccess(`WooCommerce status changed to ${status}.`);
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  /**
   * A web order's cancel stays on the order route (PUT /orders/:id/cancel), which takes the same cancel type and asked
   * time as the fulfillment cancel (SSD-7450). Its per-line `lines` contract records each Return or Keep choice, while
   * `goodsNote` remains the readable compatibility note on the order comments.
   */
  const cancelWooCommerceOrder = async () => {
    const request = await askOrderCancellation(false, true);
    if (request === null || request.cancelType === undefined) {
      return;
    }
    const { reason, cancelType, lockOverrideReason, goodsNote, lines: goodsLines } = request;
    const applied = await runAction(
      async () => {
        const cancelled = await oms.cancelOrder(
          String(header.orderId),
          {
            reason: `Cancellation reason: ${reason}`,
            operator: operatorName(),
            cancelType,
            ...(lockOverrideReason === undefined ? {} : { lockOverrideReason }),
            ...(goodsNote === undefined ? {} : { goodsNote }),
            ...(goodsLines === undefined ? {} : { lines: goodsLines }),
          },
          { operator: operatorName(), source: 'OPERATOR' },
        );
        if (!cancelled) {
          throw new Error('cancel refused');
        }
      },
      cancelledMessage(header.orderId, lockOverrideReason ?? goodsNote),
      describeOrderCancelRefusal,
    );
    if (!applied) {
      // runAction already reports the refusal.
      return;
    }
  };

  /**
   * An outbounded web order is cancelled only after its outbound is: the server refuses it with ORDER_ALREADY_SHIPPED
   * (SSD-7675). So Cancel Order takes that step first, confirmed on its own, and leaves the cancel itself to a second
   * press once the order reads READY. Only the operator knows the parcel is still in the building, and the two writes
   * are not one transaction, so they are not chained behind one click.
   */
  const cancelOutboundBeforeCancel = async () => {
    const agreed = await askConfirm({
      title: 'Cancel Order — step 1 of 2: cancel the outbound',
      message:
        `Order ${header.orderId} has already been outbounded, so its outbound has to be cancelled before the order can be. ` +
        'The order goes back to READY and its parcel stops counting as shipped. Then press Cancel Order again.',
      confirmLabel: 'Cancel outbound',
      cancelLabel: 'Keep the order',
    });
    if (!agreed) {
      return;
    }
    void runAction(
      () => client.cancelOutbound(header.orderId),
      `Outbound cancelled — order ${header.orderId} is back to READY. Press Cancel Order again to cancel it.`,
    );
  };

  /**
   * Where Cancel Order (and the status menu's CANCELLED) starts: a web order whose live lines have all been outbounded
   * takes the outbound back first (SSD-7675), any other web order goes to the order route's form, and an order
   * WooCommerce never saw cancels in fulfillment. Cancel Outbound undoes only a whole shipment (it refuses mixed live
   * states), so an order with some lines out and some not goes to the form, whose refusal now names the shipped lines.
   */
  const startCancelOrder = (): Promise<void> => {
    if (!isWooCommerceOrder(header.orderId)) {
      return cancelFulfillmentOrder();
    }
    const live = lines.filter((line) => !['CANCELLED', 'RETURNED'].includes(line.lineStatus));
    const shippedWhole = live.length > 0 && live.every((line) => line.lineStatus === 'OUTBOUNDED');
    return shippedWhole ? cancelOutboundBeforeCancel() : cancelWooCommerceOrder();
  };

  /** The fulfillment-only cancel: every standing line goes, and the server releases what they reserved. */
  const cancelFulfillmentOrder = async () => {
    if (describeFulfillmentCancelBlock(header.fulfillmentStatus, lines)) {
      return;
    }
    const request = await askOrderCancellation(lines.some(hasWritableReceivedGoods));
    if (request === null || request.cancelType === undefined) {
      return;
    }
    const { reason, receivedGoods, cancelType, lockOverrideReason } = request;
    await runAction(
      async () => {
        await client.cancelFulfillmentOrder(header.orderId, reason, receivedGoods, {
          cancelType,
          ...(lockOverrideReason === undefined ? {} : { lockOverrideReason }),
        });
      },
      cancelledMessage(header.orderId, lockOverrideReason),
      describeCancelLocked,
    );
  };

  /**
   * The dialog shows a refusal itself, beside the form it refers to, so this does not go through runAction.
   * The order is re-read afterwards: a split took units off it, and either mode adds it a clone. A 409 means
   * the order moved under the form (a line shipped, a reservation changed), so it is re-read then too.
   */
  const submitClone = async (draft: CloneOrderDraft): Promise<CloneOrderResponse> => {
    const targetOrderId = header.orderId;
    const fingerprint = JSON.stringify({ orderId: targetOrderId, ...draft });
    let result: CloneOrderResponse;
    try {
      result = await cloneKeysRef.current.run(fingerprint, (idempotencyKey) =>
        client.cloneOrder(targetOrderId, { ...draft, idempotencyKey }),
      );
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        await refreshDetail();
      }
      throw err;
    }
    if (!(await refreshDetail()) && loadedOrderIdRef.current === String(targetOrderId)) {
      showWarning(
        `Clone #${result.cloneOrderId} created. Could not refresh the order; reload before taking another action.`,
      );
    }
    return result;
  };

  const returnPutaway = async (lineNo: number) => {
    const locationCode = await ask({ title: `Return line ${lineNo}`, label: 'Location', confirmLabel: 'Put away' });
    if (locationCode === null) {
      return;
    }
    void runAction(
      () =>
        putawayKeysRef.current.run(`${header.orderId}:${lineNo}:${locationCode}`, (idempotencyKey) =>
          client.returnPutaway(header.orderId, { idempotencyKey, lineNo, locationCode }),
        ),
      `Returned line ${lineNo} put away at ${locationCode}.`,
    );
  };

  const outboundCandidates = lines.filter(
    (orderLine) => !['CANCELLED', 'RETURNED', 'CLOSED'].includes(orderLine.lineStatus),
  );
  const everyCandidateInbounded = outboundCandidates.every((orderLine) => isOutboundReady(orderLine.lineStatus));
  const carrierOutboundBlock = outboundCarrierBlock(header.orderId, tracking, pendingParcels);
  const canOutbound =
    !carrierOutboundBlock && header.holdReason == null && outboundCandidates.length > 0 && everyCandidateInbounded;

  const describePicBlock = (): string => {
    if (busy) {
      return RELOADING;
    }
    if (!selectedPic) {
      return 'Pick a PIC from the roster first';
    }
    return selectedPic === header.picUser ? 'This PIC is already assigned' : '';
  };

  const describeBulkInboundBlock = (): string => {
    if (busy) {
      return RELOADING;
    }
    return activeSelection.length === 0 ? 'Select at least one line to inbound' : '';
  };

  /** Both fields are required, so a refusal that named only the first would send the operator back for the second. */
  const describeCorrectTrackingBlock = (): string => {
    if (busy) {
      return RELOADING;
    }
    const missing: string[] = [];
    if (!trackingNumber.trim()) {
      missing.push('a tracking number');
    }
    if (!trackingCarrier.trim()) {
      missing.push('a carrier');
    }
    return missing.length === 0 ? '' : `Enter ${missing.join(' and ')}`;
  };

  const describeCommentBlock = (): string => {
    if (busy) {
      return RELOADING;
    }
    return commentText.trim() ? '' : 'Write a comment first';
  };

  /**
   * Every gate, not just the first: an operator who releases the hold only to meet a second refusal has
   * been sent back to the queue for nothing.
   */
  const describeOutboundBlock = (): string => {
    if (busy) {
      return RELOADING;
    }
    const gates: string[] = carrierOutboundBlock ? [carrierOutboundBlock] : [];
    if (header.holdReason != null) {
      gates.push('Release the hold before outbounding');
    }
    if (outboundCandidates.length === 0) {
      gates.push('No line on this order can be outbounded');
    } else if (!everyCandidateInbounded) {
      gates.push('Every remaining line must be inbounded first');
    }
    return gates.join(' · ');
  };

  // Each gate is asked once per render and reused by the button's disabled state, its tooltip and its visible reason.
  const outboundBlock = describeOutboundBlock();
  const notInWooCommerce = isWooCommerceOrder(header.orderId) ? '' : 'This order is not in WooCommerce';
  /** Undefined on a server that predates the table; an empty array means the order is genuinely finished. */
  const allowedTransitions = detail.allowedTransitions;

  const isResolved = header.fulfillmentStatus === 'RESOLVED';
  /** Lifting this verdict would not restore the lines and stock the close released, so it is final here. */
  const isClosedUnshipped = isResolved && detail.resolutionKind === 'CLOSE_UNSHIPPED';
  /** The RESOLVED row is its own reversal once the verdict is on: nothing else in the menu can move the order. */
  const statusChoices = FULFILLMENT_STATUS_CHOICES.map((choice) =>
    isResolved && !isClosedUnshipped && choice.internal === 'resolve'
      ? { ...choice, internal: 'unresolve' as const }
      : choice,
  );
  const cancelsInFulfillment = !isWooCommerceOrder(header.orderId);
  const cancelOrderBlock = cancelsInFulfillment
    ? describeFulfillmentCancelBlock(header.fulfillmentStatus, lines)
    : describeCancelBlock(header.wcStatus, header.fulfillmentStatus, lines);
  const picBlock = describePicBlock();
  const bulkInboundBlock = describeBulkInboundBlock();
  const correctTrackingBlock = describeCorrectTrackingBlock();
  const commentBlock = describeCommentBlock();

  const addressEditBlock = describeAddressEditBlock(header.fulfillmentStatus);

  const shippingBlob = readRecord(header.shipping);
  /** The row stores the PIC name, the same value Facade routing and n8n write, so it reads as is. */
  const describePic = (picUser: string | null): string => picUser || '—';
  // Options carry the roster name because picUser holds names; a nameless profile has nothing to save.
  const assignablePics = picProfiles.filter((profile) => profile.isActive && profile.name);

  const cloneSourceId = cloneSourceOf(detail);
  const lastCarrierFailure = lastCarrierFailureOf(events);
  const clones = detail.relations?.clones ?? [];
  const ownV1OrderId = detail.relations?.v1OrderId;
  /**
   * What the OMS order screen lists, in its order. A clone adds the order it was cloned from (SSD-6840),
   * and an order that was cloned lists its clones (SSD-7061); this page's own order ID stays its own.
   * A clone V1 OMS made also carries the V1 id ops know it by (`455345-1`) beside its 2.0 number (SSD-7397).
   */
  const orderInformationFields: FactField[] = [
    // Only the stored server CS status is displayed; no WC, tracking or manual-hold fallback.
    {
      label: 'CS status',
      value: header.csStatus ?? '',
      csStatus: true,
      csResolutionMethod: header.csResolutionMethod ?? null,
    },
    { label: 'Order Date', value: kstTimestamp(header.orderPlacedAt) },
    { label: 'Order Created At', value: kstTimestamp(header.createdAt) },
    { label: 'Total Items', value: String(header.totalQuantity) },
    { label: 'Total Discount', value: moneyOf(readRecord(header.amounts)['discount'], orderCurrency) },
    { label: 'Total Amount', value: moneyOf(readRecord(header.amounts)['totalAmount'], orderCurrency) },
    ...(ownV1OrderId ? [{ label: 'V1 Order ID', value: ownV1OrderId }] : []),
    ...(cloneSourceId
      ? [
          {
            label: 'Clone of',
            value: withV1OrderId(cloneSourceId, detail.relations?.cloneOf?.v1OrderId),
            href: ROUTES.fulfillment.orderDetail(cloneSourceId),
          },
        ]
      : []),
    ...(clones.length > 0
      ? [
          {
            label: 'Clones',
            value: '',
            links: clones.map((clone) => ({
              label: withV1OrderId(clone.orderId, clone.v1OrderId),
              href: ROUTES.fulfillment.orderDetail(clone.orderId),
              note: [clone.fulfillmentStatus && fulfillmentStatusLabel(clone.fulfillmentStatus), clone.mode]
                .filter(Boolean)
                .join(' · '),
            })),
          },
        ]
      : []),
  ];

  /** Columns two and three: both addresses, each opening the same editor from its own pencil. */
  const addressColumns: AddressColumn[] = [
    {
      label: 'Billing Address',
      icon: '👤',
      slot: 'billingAddress',
      fields: addressFields(shippingBlob['billingAddress'], BILLING_ADDRESS_FIELDS),
    },
    {
      label: 'Shipping Address',
      icon: '🚚',
      slot: 'shippingAddress',
      fields: addressFields(shippingBlob['shippingAddress'], SHIPPING_ADDRESS_FIELDS),
    },
  ];

  // The title row carries one label button for the order; the tracking table keeps its per-shipment ones.
  const currentOutboundParcel = currentOutboundShipmentOf(tracking, header.orderId, pendingParcels);
  const labelShipment =
    currentOutboundParcel &&
    !isPendingParcel(currentOutboundParcel) &&
    !isDeleoParcel(currentOutboundParcel) &&
    currentOutboundParcel.labelUrl
      ? currentOutboundParcel
      : undefined;
  const deleoParcel = deleoTrackingOf(tracking, header.orderId, pendingParcels);
  const deleoParcels: OutboundParcel[] = [
    ...tracking.filter((shipment) => isDeleoParcel(shipment) && isOutboundParcel(shipment, header.orderId)),
    ...(deleoParcel && isPendingParcel(deleoParcel) ? [deleoParcel] : []),
  ];
  const deleoRowRefusalOf = (shipment: OutboundParcel) =>
    !isPendingParcel(shipment) && isDeleoParcel(shipment) && !isOutboundParcel(shipment, header.orderId)
      ? 'No outbound shipping sticker for this row'
      : deleoLabelRefusalOf(shipment);
  /** The bundle is order-scoped, so a historical row cannot select its own PDF. */
  const archivedRowRefusalOf = (shipment: FulfillmentTracking) =>
    !isDeleoParcel(shipment) && shipment !== currentOutboundParcel
      ? 'Only the current outbound carrier label can be printed'
      : '';
  const deleoLabelOrderOf = (shipment: OutboundParcel) =>
    toDeleoLabelOrder({
      orderId: header.orderId,
      shipping: header.shipping,
      lines,
      tracking,
      parcel: shipment,
      isMarketing: header.isMarketing,
    });
  const isCancelled = header.fulfillmentStatus === 'CANCELLED';
  /** The carrier's label and the PACKING sheet go out together, so either one's gate holds the print, and every gate is said at once [10/13]. */
  const labelGate = isCancelled
    ? ''
    : deleoParcel
      ? deleoLabelRefusalOf(deleoParcel)
      : labelShipment
        ? ''
        : 'No label from the carrier yet';
  const printLabelBlock = busy
    ? RELOADING
    : isCancelled
      ? 'Print on a cancelled order is blocked'
      : [labelGate, packingLabelRefusal].filter((gate) => gate !== '').join(' · ');
  /** No provider routes by the destination country; a clone's dialog opens on the carrier it was made for. */
  const bookCarrierOrder = async (provider: CloneOrderProvider | undefined) => {
    setBookingCarrier(false);
    const operator = OperatorIdentity.current();
    if (!operator) {
      return;
    }
    await runAction(
      () => client.createCarrierOrder(header.orderId, { operator, ...(provider && { provider }) }),
      `Carrier order created — order ${header.orderId}.`,
      describeWebOrderBeforeLaunch('book its carrier order'),
    );
  };

  /** Reissued from the 2.0-stored address, so an address fixed on this screen is the one the new label carries. */
  const resetCarrierOrder = async () => {
    const reason = await ask({ title: 'Reset carrier order', label: 'Reason', confirmLabel: 'Reset' });
    if (reason === null) {
      return;
    }
    await runAction(
      () => client.resetCarrierOrder(header.orderId, { reason }),
      ({ created }): FulfillmentToastState => ({
        tone: 'success',
        text: `Carrier order reset — order ${header.orderId}, new waybill ${created.trackingNumber} (${created.carrier}).`,
      }),
      describeWebOrderBeforeLaunch('reset its carrier order'),
    );
  };
  /** The outbound parcel row; inbound rows reference a line (`orderId#lineNo`), not the order. */
  const outboundTracking = tracking.filter((shipment) => isOutboundParcel(shipment, header.orderId));
  /** Whether a carrier reported the order's parcel picked up, so a refunded RETURNED line can be put back [SSD-7566]. */
  const outboundLeft = outboundParcelLeft(outboundTracking);
  const holdsCarrierOrder = Boolean(currentOutboundParcel) || outboundTracking.length > 0;
  /**
   * Create is refused only where the outbound row already carries a carrier order id, so a hand-entered waybill
   * (null id) is booked over. A row from a server that omits the id is read as booked, which keeps Reset.
   * Mirrors SkinSeoulFacade packages/core/src/domain/fulfillment/booking/CreateFulfillmentCarrierOrderHandler.ts:82-85
   * (master), which answers 409 only when the outbound row has a truthy `carrierOrderId` and otherwise books onto
   * that row.
   */
  const holdsBookedCarrierOrder =
    currentOutboundParcel && isPendingParcel(currentOutboundParcel)
      ? Boolean(currentOutboundParcel.carrierOrderId?.trim())
      : outboundTracking.some((shipment) => shipment.carrierOrderId !== null && shipment.carrierOrderId !== '');
  /**
   * An order with no booked carrier order books its first one: a 2.0-born order always, a web order once 2.0 has
   * launched (before that OMS Center books it). Every other order reissues the one it has.
   */
  const booksFirstCarrierOrder = !holdsBookedCarrierOrder && (isBornInFulfillment2(header.orderId) || launch === 'on');
  /** A switch this browser sent and has not seen finish; its dialog must stay reachable even after the cancel. */
  const keptSwitch = readCarrierSwitchDraft(header.orderId);
  /** Before launch OMS Center owns a web order's carrier order, so the 2.0 carrier actions are refused there. */
  const webOrderAwaitsLaunch = isWooCommerceOrder(header.orderId) && launch !== 'on';
  const describeSwitchBlock = (): string => {
    if (busy) {
      return RELOADING;
    }
    if (webOrderAwaitsLaunch) {
      return launch === 'unknown'
        ? 'Checking whether Fulfillment 2.0 has launched'
        : 'Web order before launch — switch its carrier in OMS Center';
    }
    return keptSwitch || SWITCHABLE_FULFILLMENT_STATUSES.includes(header.fulfillmentStatus)
      ? ''
      : `Only an order that has not shipped can switch carrier (this one is ${fulfillmentStatusLabel(header.fulfillmentStatus)})`;
  };
  const switchBlock = describeSwitchBlock();
  /** Reset and Create are refused on a web order before launch exactly as the switch is, so they wait the same way. */
  const resetBlock = busy
    ? RELOADING
    : webOrderAwaitsLaunch
      ? launch === 'unknown'
        ? 'Checking whether Fulfillment 2.0 has launched'
        : 'Web order before launch — reset its carrier order in OMS Center'
      : '';

  /** A kept switch reopens as it was sent; a new one first reads the carrier order the order holds now. */
  const openSwitch = async () => {
    if (readCarrierSwitchDraft(header.orderId)) {
      setSwitching({ source: null });
      return;
    }
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    const targetOrderId = header.orderId;
    try {
      const shipment = await client.getOrderShipment(targetOrderId);
      if (loadedOrderIdRef.current !== String(targetOrderId)) {
        return;
      }
      if (!shipment?.providerOrderId || !shipment.provider) {
        showError('This order holds no carrier order to switch. Use Reset Order to book one.', undefined, AUTO_DISMISS);
        return;
      }
      setSwitching({ source: { carrier: shipment.provider, carrierOrderId: shipment.providerOrderId } });
    } catch (err) {
      if (loadedOrderIdRef.current === String(targetOrderId)) {
        showError(serverRefusal(err) ?? 'Could not read the carrier order. Reload the page and try again.');
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  /**
   * The dialog words its own refusals, beside the key it decides to keep or drop. The order is re-read
   * either way: a refusal may come after the cancel, and the screen must not show a waybill that is gone.
   */
  const submitSwitch = async (request: SwitchCarrierOrderRequest): Promise<SwitchCarrierOrderResponse> => {
    const targetOrderId = header.orderId;
    let result: SwitchCarrierOrderResponse;
    try {
      result = await client.switchCarrierOrder(targetOrderId, request);
    } catch (err) {
      await refreshDetail();
      throw err;
    }
    const refreshed = await refreshDetail();
    if (loadedOrderIdRef.current === String(targetOrderId)) {
      const { cancelled, created } = result;
      showSuccess(
        `Carrier switched — order ${targetOrderId}, ${cancelled.carrier} ${cancelled.carrierOrderId} cancelled, new waybill ${created.trackingNumber} (${created.carrier}).` +
          (result.replayed ? ' This switch had already finished; no carrier was called again.' : ''),
      );
      if (!refreshed) {
        showWarning('Could not refresh the order; reload before taking another action.');
      }
    }
    return result;
  };

  /**
   * The archived pdf printed from a frame on this page. A label the archive cannot merge (a raw carrier format)
   * is said so rather than opened in a tab: no print on this desk opens one [10/16].
   */
  const printArchivedLabel = async (trackingNumber: string) => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    try {
      // The sheet behind the label is the PACKING sheet View Orders prints, read with the pdf so the two go out together [SSD-7207].
      const [bundle, invoice] = await Promise.all([
        client.getLabelBundle([header.orderId]),
        client.getPackingInvoice(header.orderId).catch(() => null),
      ]);
      if (loadedOrderIdRef.current !== String(header.orderId)) {
        return;
      }
      if (bundle.skipped.includes(header.orderId)) {
        showError(NO_ARCHIVED_LABEL_TOAST);
        return;
      }
      const [result] = await printParcels(printer, [
        { orderId: header.orderId, trackingNumber, pdf: bundle.pdf, invoice },
      ]);
      reportParcelPrint(result!, 'Carrier label print job submitted.');
    } catch (err) {
      if (loadedOrderIdRef.current === String(header.orderId)) {
        showError(archiveHasNothing(err) ? NO_ARCHIVED_LABEL_TOAST : PRINT_FAILED_TOAST);
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const printDrawnLabel = async (trackingNumber: string) => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    try {
      // The sheet behind the sticker is the PACKING sheet View Orders prints; a failed read prints the sticker alone and says so.
      const invoice = await client.getPackingInvoice(header.orderId).catch(() => null);
      // The desk may have moved to another order while the sheet was read; that order's sticker is not this click's.
      if (loadedOrderIdRef.current !== String(header.orderId)) {
        return;
      }
      // The sticker as the hidden sheet drew it, laid into one document with the PACKING sheet behind it [10/13].
      const selectedSticker = Array.from(
        deleoLabelRef.current?.querySelectorAll<HTMLElement>('[data-deleo-label-number]') ?? [],
      ).find((node) => node.dataset['deleoLabelNumber'] === trackingNumber);
      const waybill = selectedSticker?.querySelector('.print-area')?.outerHTML ?? '';
      const [result] = await printParcels(printer, [{ orderId: header.orderId, trackingNumber, waybill, invoice }]);
      reportParcelPrint(result!, 'Carrier label print job submitted.');
    } catch {
      if (loadedOrderIdRef.current === String(header.orderId)) {
        showError(PRINT_FAILED_TOAST);
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  /** The PACKING sheet on its own, printed at once from a frame on this page — no tab, no preview [10/16]. */
  const printPackingInvoice = async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    try {
      // Re-read on the click: a sheet left open on the bench may have been amended since [SSD-6352].
      const invoice = await client.getPackingInvoice(header.orderId);
      if (loadedOrderIdRef.current !== String(header.orderId)) {
        return;
      }
      // An invoice with no pages or a sheet the renderer refused is said in its own words, not as a generic failure.
      const built = packingSheetJob(invoice);
      if (!built.job) {
        showError(built.reason);
        return;
      }
      const outcome = await printer.dispatch(built.job);
      if (outcome.ok) {
        showSuccess('PACKING print job submitted.');
      } else {
        showError(PrintFailureNotice.textOf(outcome));
      }
    } catch (err) {
      if (loadedOrderIdRef.current === String(header.orderId)) {
        showError(describeFailure(err, 'The PACKING sheet could not be printed.'));
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const printCarrierLabel = async (shipment: FulfillmentTracking) => {
    if (isDeleoParcel(shipment)) {
      if (!deleoRowRefusalOf(shipment)) {
        await printDrawnLabel(deleoLabelNumberOf(shipment));
      }
      return;
    }
    if (archivedRowRefusalOf(shipment)) {
      return;
    }
    await printArchivedLabel(shipment.number);
  };

  const openShippingLabel = () => {
    if (printLabelBlock !== '') {
      return;
    }
    if (deleoParcel) {
      void printDrawnLabel(deleoLabelNumberOf(deleoParcel));
      return;
    }
    if (labelShipment) {
      void printArchivedLabel(labelShipment.number);
    }
  };
  const carrierName =
    currentOutboundParcel && isPendingParcel(currentOutboundParcel)
      ? currentOutboundParcel.carrier
      : (tracking.find((shipment) => shipment.carrier)?.carrier ?? null);

  const sortedEvents = [...events].sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime());
  // Cancelled lines stay on screen as history but leave the count and the total: they are not the order.
  const liveLines = lines.filter((line) => line.lineStatus !== 'CANCELLED');
  /** Read from the order on screen, so a reload behind the open dialog shows what is stored now. */
  const sourcingLine = sourcingLineNo === null ? undefined : lines.find((line) => line.lineNo === sourcingLineNo);

  return (
    <div className="wms-wireframe wms-order-detail">
      {/* One feedback slot for every write on this screen, success and refusal alike [T03]. */}
      <FulfillmentToastHost toasts={toasts} onDismiss={dismissToast} />
      {/* A status push waits on WooCommerce for seconds; disabled buttons alone read as a frozen page. */}
      {busy && (
        <div className="wms-busy-overlay" role="status" aria-live="polite" data-testid="page-busy-overlay">
          <div className="wms-busy-ring" />
          <span className="wms-busy-label">Working — waiting for the order to come back</span>
        </div>
      )}
      {/*
        Three zones, per the approved wireframe: the way back and the WooCommerce actions above the
        title, the order and what prints it on the title line, and Outbound at the foot of the page
        where an order-level action belongs. One flat row of eleven buttons had no reading order,
        and the disabled Outbound dragged its reason through the middle of it.
      */}
      {/*
        The reasons sit under the row rather than under each button [BR-46]: a two-line reason inside
        one button's column grew that column and stretched the three buttons beside it. Each note
        names the actions it refuses, since down here the reader can no longer tell by position.
      */}
      {/* .subbar — leaving the order is navigation, not an action on it, so it stands alone. */}
      <div data-testid="detail-nav-row" className="subbar">
        <Link to={ROUTES.fulfillment.orders} className="back">
          ← Back to Orders
        </Link>
        {/*
          View Orders is where inbound and outbound are worked; this saves typing the number in again. It opens
          the order by its address, the one a reload keeps, so the number never goes through a search [SSD-7296].
        */}
        <Link to={ROUTES.fulfillment.viewOrdersOrder(header.orderId)} className="back">
          → Open in View Orders
        </Link>
      </div>

      {/* .otitle — the order, its status, the carrier, and what prints or restates it. */}
      {deleoParcels.some((shipment) => !deleoRowRefusalOf(shipment)) && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }} aria-hidden="true">
          <div ref={deleoLabelRef}>
            {deleoParcels.map((shipment, index) => {
              const number = deleoLabelNumberOf(shipment);
              if (deleoRowRefusalOf(shipment)) {
                return null;
              }
              const order = deleoLabelOrderOf(shipment);
              return (
                <div key={`${number}-${index}`} data-deleo-label-number={number}>
                  <OrderPdfPreview order={order} moreNote={deleoLabelMoreNote(order)} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div data-testid="detail-title-row" className="otitle">
        <h2>Order # {header.orderId}</h2>
        <span
          className={`status ${wireframeStatusClass(header.fulfillmentStatus, header.holdReason)}`}
          title={header.holdReason != null ? `Manual hold: ${header.holdReason}` : 'Warehouse fulfillment status'}
        >
          {titleChipLabel(header.fulfillmentStatus, header.holdReason, detail.resolutionKind)}
        </span>
        {header.holdReason != null && (
          <span
            className={`status ${wireframeStatusClass(header.fulfillmentStatus, null)}`}
            title="Warehouse fulfillment status"
          >
            {fulfillmentStatusLabel(header.fulfillmentStatus, detail.resolutionKind)}
          </span>
        )}
        <CancelLockBadge lock={header} variant="header" />
        {carrierName && <span className="carrier">{carrierName}</span>}
        <ShelfField
          id="order-detail-shelf"
          value={shelf}
          dirty={shelfDirty}
          saved={shelfSaved}
          disabled={busy}
          onChange={(value) => {
            shelfEditRef.current = header.orderId;
            shelfTypingRef.current += 1;
            setShelf(value);
            setShelfDirty(true);
            setShelfSaved(false);
          }}
          onSave={() => void saveShelf()}
        />
        <PackingVideosChip state={packingVideos.state} />
      </div>

      {/* A cancelled order says what the order team chose for its goods, so the warehouse need not ask (SSD-7533). */}
      {isCancelledOrder(header) && (
        <div className="cancelled-goods-banner">
          <CancelledGoodsNote events={events} lines={lines} />
        </div>
      )}

      {/*
        One panel for everything you can do to this order. They were split across the subbar and the
        title line, which read as two unrelated groups and left the title row longer than its own name.
        Cancel sits last and apart: it is the one that cannot be taken back.
      */}
      <GigOrderDetailModal
        client={gigWorkerClient}
        reference={openGigOrderId && openGigOrderId === orderId ? (gigOrderReference ?? null) : null}
        onHide={() => setOpenGigOrderId(null)}
      />
      <div data-testid="detail-action-panel" className="oactions">
        <GigOrderDetailAction
          reference={gigOrderReference}
          lookupFailed={isGigOrderLookupError}
          onOpen={() => setOpenGigOrderId(orderId ?? null)}
          onRetry={() => void refetchGigOrderReference()}
        />
        {/* Both kinds of order have a trail now: the store's, or the fulfillment event log [T08]. */}
        <button type="button" className="btn btn-line btn-sm" onClick={openAuditHistory}>
          ↻ Audit History
        </button>
        <button
          type="button"
          className="btn btn-line btn-sm"
          disabled={notInWooCommerce !== ''}
          title={notInWooCommerce}
          onClick={openWooCommerceAdmin}
        >
          ↗ View in WP
        </button>
        <button type="button" className="btn btn-line btn-sm" disabled={busy} onClick={() => setCloning(true)}>
          ⧉ Clone Order
        </button>
        {/* One print for the parcel: the carrier's label with our PACKING sheet behind it, never one without the other [10/13]. */}
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-label="🖨 Print Label"
          disabled={printLabelBlock !== ''}
          title={printLabelBlock}
          onClick={openShippingLabel}
        >
          🖨 Print Label
        </button>
        {/* The PACKING sheet on its own, printed on the press [10/16]. */}
        <button
          type="button"
          className="btn btn-line btn-sm"
          disabled={busy}
          onClick={() => void printPackingInvoice()}
        >
          🧾 Packing Invoice
        </button>
        <span className="ddwrap">
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy}
            onClick={() => setStatusMenuOpen((open) => !open)}
          >
            Change Status ▾
          </button>
          {statusMenuOpen && (
            <div className="statusdd" role="menu">
              {statusChoices.map((choice) => {
                const block = statusChoiceBlock(choice);
                return (
                  <button
                    type="button"
                    key={choice.status}
                    className={header.fulfillmentStatus === choice.status ? 'on' : undefined}
                    disabled={busy || block !== ''}
                    title={block}
                    aria-label={describeStatusChoice(choice)}
                    aria-describedby={
                      choice.action === 'outbound' && carrierOutboundBlock ? 'status-outbound-carrier-block' : undefined
                    }
                    onClick={() => {
                      setStatusMenuOpen(false);
                      void applyStatusChoice(choice);
                    }}
                  >
                    {describeStatusChoice(choice)}
                    {choice.action === 'outbound' && carrierOutboundBlock && (
                      <span id="status-outbound-carrier-block" className="blocked-note d-block">
                        {carrierOutboundBlock}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* Offered only before outbound: it ends an order that never shipped [SSD-7056]. */}
              {canCloseUnshipped(header.fulfillmentStatus) && (
                <button
                  type="button"
                  disabled={busy}
                  title="Close this order without shipping it; the stock it holds is released"
                  onClick={() => {
                    setStatusMenuOpen(false);
                    void submitCloseUnshipped();
                  }}
                >
                  {CLOSED_UNSHIPPED_LABEL}
                </button>
              )}
              {/* A hold is a status the operator sets, so it lives with the other statuses [F Hold]. */}
              {header.holdReason == null && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setStatusMenuOpen(false);
                    void submitHold();
                  }}
                >
                  Hold order…
                </button>
              )}
            </div>
          )}
        </span>
        <span role="group" aria-label="CS status actions" className="d-inline-flex align-items-center gap-2">
          <span className="small text-muted">CS status:</span>
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy}
            title="CS display and audit only; does not change warehouse, carrier or WooCommerce"
            onClick={() => void markCsStatus('delivered')}
          >
            Mark delivered
          </button>
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy}
            title="Mark CS canceled; eligible orders can also cancel warehouse work after confirmation"
            onClick={() => void markCsStatus('canceled')}
          >
            Mark canceled
          </button>
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy}
            title="Records how CS resolved the issue; CS display and audit only"
            onClick={() => void markCsResolved()}
          >
            Mark resolved…
          </button>
        </span>
        <span className="oactions-gap" />
        <button
          type="button"
          className="btn btn-red-line btn-sm"
          disabled={busy || cancelOrderBlock !== ''}
          title={cancelOrderBlock}
          onClick={() => void startCancelOrder()}
        >
          ✕ Cancel Order
        </button>
        {busy && <span className="blocked-note">{RELOADING}</span>}
      </div>

      <AuditHistoryModal
        show={auditOpen}
        orderId={String(header.orderId)}
        inWooCommerce={isWooCommerceOrder(header.orderId)}
        onHide={() => setAuditOpen(false)}
      />

      {header.holdReason != null && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center gap-3">
          <div>
            <span className="fw-semibold">On hold</span> — {header.holdReason}
            <div className="small">
              by {header.heldBy ?? '—'} · {kstTimestamp(header.heldAt)}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={busy}
            onClick={() =>
              void runAction(() => client.releaseHold(header.orderId), `Order ${header.orderId} released from hold.`)
            }
          >
            Release hold
          </button>
        </Alert>
      )}

      {/*
        A row saved at the foot of a long table was answered by a banner at the top of the page, which
        on a 40-line order is a screen and a half away: the API's reason for refusing went unread and
        the save looked like it did nothing. Failures now sit against the viewport until dismissed.
      */}
      {/* .info3 — three columns: the order's own facts, then the two addresses, each editable in place. */}
      <div className="info3" data-testid="order-information">
        <div>
          <h3>📋 Order Information</h3>
          {orderInformationFields.map((field) => (
            <div className="kv" key={field.label}>
              <div className="k">{field.label}</div>
              <div className="v">
                {field.csStatus ? (
                  field.value ? (
                    <CsStatusBadge status={field.value} resolutionMethod={field.csResolutionMethod} />
                  ) : null
                ) : field.links ? (
                  field.links.map((link) => (
                    <div key={link.href}>
                      <Link to={link.href}>{link.label}</Link>
                      {link.note && <span className="text-muted small"> {link.note}</span>}
                    </div>
                  ))
                ) : field.href ? (
                  <Link to={field.href}>{field.value}</Link>
                ) : (
                  field.value
                )}
              </div>
            </div>
          ))}
          {/* OMS shows the value with a pencil; the picker only appears once you ask for it. */}
          <div className="kv">
            {/* OMS puts the pencil beside the label, not the value, so the value reads as just the name. */}
            <div className="k" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              PIC
              <button
                type="button"
                className="edit"
                aria-label="Edit PIC"
                disabled={busy}
                onClick={() => setEditingPic((editing) => !editing)}
              >
                ✎
              </button>
            </div>
            <div className="v" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{describePic(header.picUser)}</span>
              {editingPic && (
                <>
                  <Form.Select
                    size="sm"
                    aria-label="PIC"
                    style={{ width: 'auto' }}
                    value={selectedPic}
                    onChange={(event) => setSelectedPic(event.target.value)}
                  >
                    <option value="">Select PIC</option>
                    {header.picUser && !assignablePics.some((profile) => profile.name === header.picUser) && (
                      <option value={header.picUser}>{header.picUser} (current, inactive)</option>
                    )}
                    {assignablePics.map((profile) => (
                      <option key={profile.picId} value={profile.name}>
                        {profile.name}
                      </option>
                    ))}
                  </Form.Select>
                  <button
                    type="button"
                    className="btn btn-line btn-sm"
                    disabled={busy || !selectedPic || selectedPic === header.picUser}
                    title={picBlock}
                    onClick={assignPic}
                  >
                    Assign PIC
                  </button>
                  {header.picUser && (
                    <button
                      type="button"
                      className="btn btn-red-line btn-sm"
                      disabled={busy}
                      onClick={() => void clearPic()}
                    >
                      Clear PIC
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {addressColumns.map(({ label, icon, slot, fields }) => (
          <div key={label} role="region" aria-label={label}>
            <h3>
              {icon} {label}{' '}
              <button
                type="button"
                className="edit"
                aria-label={`Edit ${label}`}
                disabled={busy || addressEditBlock !== ''}
                title={addressEditBlock}
                onClick={() => openAddressForm(slot)}
              >
                ✎
              </button>
            </h3>
            {/* A sealed order shows no editor, even one left open from before it was sealed: its save is refused. */}
            {editingAddress === slot && addressEditBlock === '' ? (
              <div className="addr-edit">
                {addressFieldsFor(slot).map(
                  ({ key, label: fieldLabel, editLabel, placeholder, maxLength, normalize }) => {
                    const fieldError =
                      describeAddressField(key, addressDraft.address[key] ?? '') ?? addressRefusals[key] ?? null;
                    return (
                      <Form.Group className="kv" controlId={`${slot}-${key}`} key={key}>
                        <Form.Label className="k mb-0">{editLabel ?? fieldLabel}</Form.Label>
                        <Form.Control
                          size="sm"
                          isInvalid={fieldError !== null}
                          placeholder={placeholder}
                          maxLength={maxLength}
                          value={addressDraft.address[key] ?? ''}
                          onChange={(e) => {
                            const value = normalize ? normalize(e.target.value) : e.target.value;
                            // The server's verdict was about the old value, so it goes the moment one is typed.
                            setAddressRefusals((prev) => {
                              const next = { ...prev };
                              delete next[key];
                              return next;
                            });
                            setAddressDraft((draft) => ({
                              ...draft,
                              address: { ...draft.address, [key]: value },
                            }));
                          }}
                        />
                        {fieldError && <Form.Control.Feedback type="invalid">{fieldError}</Form.Control.Feedback>}
                      </Form.Group>
                    );
                  },
                )}
                {slot === 'shippingAddress' && (
                  <>
                    <Form.Group className="kv" controlId="shipping-message">
                      <Form.Label className="k mb-0">Shipping Message</Form.Label>
                      <Form.Control
                        size="sm"
                        value={addressDraft.shippingMessage}
                        onChange={(e) => setAddressDraft((draft) => ({ ...draft, shippingMessage: e.target.value }))}
                      />
                    </Form.Group>
                    {CONSIGNEE_FIELDS.map(({ key, label: fieldLabel }) => (
                      <Form.Group className="kv" controlId={`consignee-${key}`} key={key}>
                        <Form.Label className="k mb-0">{fieldLabel}</Form.Label>
                        <Form.Control
                          size="sm"
                          value={addressDraft.consignee[key] ?? ''}
                          onChange={(e) =>
                            setAddressDraft((draft) => ({
                              ...draft,
                              consignee: { ...draft.consignee, [key]: e.target.value },
                            }))
                          }
                        />
                      </Form.Group>
                    ))}
                  </>
                )}
                <div className="addr-actions">
                  <button
                    type="button"
                    className="link-btn blue"
                    disabled={busy || addressUnchanged || addressErrors.length > 0}
                    onClick={() => void submitAddress()}
                  >
                    Save
                  </button>
                  <button type="button" className="link-btn" onClick={() => setEditingAddress(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              fields.map((field) => (
                <div className="kv" key={field.label}>
                  <div className="k">{field.label}</div>
                  <div className="v">{field.value}</div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>

      {/* .sec — comments sit directly under the three columns, list on the left, composer on the right. */}
      <div className="sec">
        <h3>💬 Operator Comments</h3>
        <div className="cmt-wrap">
          <div className="cmt-list">
            {/*
              The whole thread, line memos included: a memo written in a line's Memo cell also lands here, tagged with
              its line, so one written there is not taken for lost (SSD-7441). View Orders still reads memos under
              their line only.
            */}
            {comments.map((comment, index) => (
              <div
                className={`c-item${isLineMemo(comment) ? ' c-memo' : ''}`}
                key={`${comment.at}-${comment.author}-${index}`}
              >
                <span className="who">{comment.author}</span>
                {isLineMemo(comment) && (
                  <span
                    className="c-line"
                    title={`A memo on line ${comment.lineNo}; it also shows in that line's Memo cell`}
                  >
                    Line {comment.lineNo}
                  </span>
                )}
                <span className="c-text">
                  <LinkedText text={comment.text} />
                </span>
                <time>{kstTimestamp(comment.at)}</time>
                <button
                  type="button"
                  className="cmt-x"
                  aria-label={
                    isLineMemo(comment)
                      ? `Delete line ${comment.lineNo} memo: ${comment.text}`
                      : `Delete comment: ${comment.text}`
                  }
                  title={isLineMemo(comment) ? 'Delete this memo' : 'Delete comment'}
                  disabled={busy}
                  onClick={() => void deleteComment(comment, isLineMemo(comment) ? comment.lineNo : undefined)}
                >
                  ✕
                </button>
              </div>
            ))}
            {comments.length === 0 && <div className="blocked-note">No comments yet.</div>}
          </div>
          <div className="cmt-new">
            <textarea
              id="new-order-comment"
              aria-label="New comment"
              placeholder="Write a comment — @name to notify via Slack (order no. · text · time · author included). Per-order history accumulates here."
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-blue btn-sm"
              style={{ marginTop: 8 }}
              disabled={commentBlock !== ''}
              title={commentBlock}
              onClick={() => void addComment()}
            >
              Add Comment
            </button>
            {commentBlock !== '' && <div className="blocked-note">{commentBlock}</div>}
          </div>
        </div>
      </div>

      {/* .sec — the order's Zero Packing videos, before the parcels they were packed into (SSD-7372). */}
      <PackingVideosSection
        client={client}
        orderId={header.orderId}
        state={packingVideos.state}
        onReload={() => void packingVideos.reload()}
        variant="ff2"
      />

      {/* .sec — the carrier badge and its corrections sit above SHIPMENT DETAILS, per the wireframe. */}
      <div className="sec" data-testid="fulfillment-tracking">
        <h3>📦 Fulfillment Tracking</h3>
        <div className="ftbadge">
          {carrierName && <span className="carrier">{carrierName}</span>}
          {/* Shut by default, like OMS: the fields open only when the operator asks to change them. */}
          <button type="button" className="btn btn-line btn-sm" disabled={busy} onClick={toggleTrackingEditor}>
            ✎ Change Tracking #
          </button>
          {/* Only where there is a waybill to take off; an order with no parcel has nothing to clear. */}
          {tracking.length > 0 && (
            <button
              type="button"
              className="btn btn-red-line btn-sm"
              disabled={busy}
              onClick={() => void clearTracking()}
            >
              ✕ Clear tracking
            </button>
          )}
          {/* An order with no booked outbound carrier order books its first one through Create; any other
              order reissues the one it holds through Reset. */}
          <button
            type="button"
            className="btn btn-sm"
            style={{ border: '1px solid var(--amber-line)', background: 'var(--page)', color: 'var(--amber)' }}
            disabled={resetBlock !== ''}
            title={resetBlock}
            onClick={() => (booksFirstCarrierOrder ? setBookingCarrier(true) : void resetCarrierOrder())}
          >
            {booksFirstCarrierOrder ? 'Create Carrier Order' : 'Reset Order'}
          </button>
          {/* Only where there is a carrier order to cancel, or a switch this browser must be able to resume. */}
          {(holdsCarrierOrder || keptSwitch) && (
            <button
              type="button"
              className="btn btn-red-line btn-sm"
              disabled={switchBlock !== ''}
              title={switchBlock}
              onClick={() => void openSwitch()}
            >
              ⇄ {keptSwitch ? 'Resume Switch Provider' : 'Switch Provider'}
            </button>
          )}
        </div>
        {webOrderAwaitsLaunch && launch === 'off' ? (
          <div className="blocked-note" data-testid="web-order-before-launch">
            Web order before launch: until Fulfillment 2.0 launches, OMS Center owns this order&apos;s carrier order, so
            Reset Order and Switch Provider are refused here.{' '}
            <Link to={ROUTES.oms.orderDetail(String(header.orderId))}>Open #{header.orderId} in OMS Center</Link>
          </div>
        ) : (
          (holdsCarrierOrder || keptSwitch) &&
          switchBlock !== '' &&
          switchBlock !== RELOADING && <div className="blocked-note">{switchBlock}</div>
        )}

        {editingTracking && (
          <div className="ftbadge">
            {/* Two bare boxes side by side say nothing about which is which; aria-label reaches a reader, not an eye. */}
            <label className="k" htmlFor="tracking-number">
              Tracking number
            </label>
            <input
              id="tracking-number"
              className="ed-in"
              style={{ maxWidth: 220 }}
              placeholder="YT2623023500101865"
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
            />
            <label className="k" htmlFor="tracking-carrier">
              Carrier
            </label>
            <input
              id="tracking-carrier"
              className="ed-in"
              style={{ maxWidth: 150 }}
              placeholder="YUN_EXPRESS"
              value={trackingCarrier}
              onChange={(event) => setTrackingCarrier(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-line btn-sm"
              disabled={correctTrackingBlock !== ''}
              title={correctTrackingBlock}
              onClick={() => void correctTracking()}
            >
              Save
            </button>
            {correctTrackingBlock !== '' && <span className="blocked-note">{correctTrackingBlock}</span>}
          </div>
        )}

        {/* The toast clears itself; the order this waybill also names has to stay readable on the card. */}
        {trackingInUse && (
          <div className="ftbadge">
            <span className="blocked-note">
              This waybill is also on order{' '}
              <Link to={ROUTES.fulfillment.orderDetail(trackingInUse.orderId)}>{trackingInUse.orderId}</Link>
            </span>
          </div>
        )}

        <PendingParcelNotice parcel={currentOutboundParcel} />
        <div className="minih">SHIPMENT DETAILS</div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Number</th>
              <th>Carrier</th>
              <th>Kind</th>
              <th>Ref id</th>
              <th>Unified status</th>
              <th>Label</th>
            </tr>
          </thead>
          <tbody>
            {tracking.map((shipment, index) => (
              <tr key={`${shipment.number}-${index}`}>
                <td className="num">
                  {/* The waybill opens the order in View Orders, the same address "Open in View Orders" uses [SSD-7296]. */}
                  <Link to={ROUTES.fulfillment.viewOrdersOrder(header.orderId)} title="Open in View Orders">
                    {shipment.number}
                  </Link>
                </td>
                <td>{shipment.carrier}</td>
                <td>{shipment.kind}</td>
                <td className="num">{shipment.refId}</td>
                <td>
                  <StatusBadge status={shipment.unifiedStatus} />
                </td>
                <td>
                  {/* Hiding the control would read as "no such thing"; the reason tells an operator whether to wait or chase the carrier. */}
                  <button
                    type="button"
                    className="btn btn-line btn-sm"
                    disabled={
                      isCancelled ||
                      archivedRowRefusalOf(shipment) !== '' ||
                      deleoRowRefusalOf(shipment) !== '' ||
                      (!shipment.labelUrl && !isDeleoParcel(shipment)) ||
                      packingLabelRefusal !== '' ||
                      busy
                    }
                    title={
                      isCancelled
                        ? 'Print on a cancelled order is blocked'
                        : archivedRowRefusalOf(shipment) ||
                          deleoRowRefusalOf(shipment) ||
                          packingLabelRefusal ||
                          undefined
                    }
                    aria-label="🖨 Print Label"
                    onClick={() => void printCarrierLabel(shipment)}
                  >
                    🖨 Print Label
                  </button>
                  {isCancelled ? (
                    <div className="blocked-note">Print on a cancelled order is blocked</div>
                  ) : archivedRowRefusalOf(shipment) ? (
                    <div className="blocked-note">{archivedRowRefusalOf(shipment)}</div>
                  ) : deleoRowRefusalOf(shipment) ? (
                    <div className="blocked-note">{deleoRowRefusalOf(shipment)}</div>
                  ) : !shipment.labelUrl && !isDeleoParcel(shipment) ? (
                    <div className="blocked-note">No label from the carrier yet</div>
                  ) : packingLabelRefusal !== '' ? (
                    <div className="blocked-note">{packingLabelRefusal}</div>
                  ) : null}
                </td>
              </tr>
            ))}
            {/* Inbound rows are the lines' own parcels; the order still waits on its outbound one. */}
            {(!holdsBookedCarrierOrder || carrierOutboundBlock) && (
              <tr>
                <td colSpan={6} className="blocked-note" style={{ textAlign: 'center', padding: '18px 0' }}>
                  {holdsBookedCarrierOrder && carrierOutboundBlock
                    ? carrierOutboundBlock
                    : tracking.length === 0
                      ? 'No tracking numbers yet.'
                      : holdsCarrierOrder
                        ? 'No carrier order booked yet.'
                        : 'No outbound tracking number yet.'}
                  {lastCarrierFailure && (
                    <div className="mt-1" data-testid="carrier-order-failed">
                      Carrier label failed: {lastCarrierFailure.message}
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <CarrierTrackingCards
          orderId={header.orderId}
          tracking={tracking}
          client={client}
          liveTrace={webOrderAwaitsLaunch ? (launch === 'off' ? 'awaits-launch' : 'checking') : 'allowed'}
        />
      </div>

      {/* .sec — the line table, then a footer that carries the order-level actions the wireframe puts there. */}
      <div className="sec" data-testid="line-items">
        {/*
          The same way into View Orders as the top row, kept where the lines are read [SSD-7296]. It sits beside
          the heading, not inside it, so the heading still reads as the section name alone.
        */}
        <div className="sec-head">
          <h3>🧾 Line Items ({liveLines.length})</h3>
          <Link className="btn btn-line btn-sm" to={ROUTES.fulfillment.viewOrdersOrder(header.orderId)}>
            → Open in View Orders
          </Link>
          {/* A cost far above what the product sells for goes to the catalog team, who set the price [SSD-7328]. */}
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={lines.length === 0}
            onClick={() => setCogsReportOpen(true)}
          >
            COGS Report
          </button>
          {/* Anything else the catalog team should change about a line: OOS, a sourcing link, product info [SSD-7384]. */}
          <button
            type="button"
            className="btn btn-line btn-sm"
            disabled={lines.length === 0}
            onClick={() => setCatalogRequestOpen(true)}
          >
            Catalog Request
          </button>
        </div>
        <p className="liinfo">
          Edit a line via the Edit button. Use checkboxes to select items for bulk Inbound. * = WooCommerce original
          before variation-pack recalculation · 🤖 = auto-filled by Agent · On a narrow screen, scroll right to see the
          Inventory fields.
        </p>
        <div className="litable-wrap">
          <table className="litable" aria-label="Line items">
            <colgroup>
              {LINE_ITEM_COLUMN_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <CheckCell as="th" rowSpan={2}>
                  <Form.Check
                    type="checkbox"
                    aria-label="Select all lines"
                    checked={selectableLineNos.length > 0 && activeSelection.length === selectableLineNos.length}
                    onChange={toggleAllLines}
                  />
                </CheckCell>
                <th rowSpan={2}>SKU</th>
                <th colSpan={5} style={{ textAlign: 'center' }}>
                  Product Information (WooCommerce)
                </th>
                <th colSpan={8} style={{ textAlign: 'center' }}>
                  Inventory
                </th>
                <th rowSpan={2}>Memo</th>
                <th rowSpan={2}>Actions</th>
              </tr>
              <tr>
                <th>Product Name</th>
                <th>Product Name KR</th>
                <th>Size</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Subtotal / Total ({orderCurrency})</th>
                <th style={{ textAlign: 'right' }} aria-label="Latest Inventory Count" title="Latest Inventory Count">
                  Stock
                </th>
                <th>Inbound Status</th>
                <th>Sourcing Route</th>
                <th>Order No. / Date</th>
                <th style={{ textAlign: 'right' }}>Product Cost 🤖</th>
                <th>Tracking Number</th>
                <th className="delivery-company-cell">Delivery Company</th>
                <th>CP Link</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const lineSku = (line.sku ?? '').trim();
                const imageHref = safeHref(line.imageUrl);
                const recalculated = lineRecalculated(line);
                const purchaseHref = safeHref(line.purchaseUrl);
                const memos = lineMemos(comments, line.lineNo);
                const lineBlocks = {
                  inbound: describeLineInboundBlock(line.lineStatus),
                  cancelInbound: describeCancelInboundBlock(line.lineStatus, line.inboundReversible),
                  cancelLine: describeCancelLineBlock(line.lineStatus),
                  edit: describeLineEditBlock(line.lineStatus),
                  quantity: describeLineQuantityBlock(line.lineStatus),
                  split: describeLineSplitBlock(line),
                  putaway: describeReturnPutawayBlock(line.lineStatus, outboundLeft),
                };
                const existingStockOffered = offersLineToExistingInventory(line, detail);
                const lineToJitOffered = offersLineToJit(line);
                const lineToJitBlock = lineToJitOffered ? describeLineToJitBlock(line, header.fulfillmentStatus) : '';
                // A menu that opens onto five refusals is worse than a button that says it is shut.
                const everyFoldedActionBlocked =
                  lineBlocks.cancelInbound !== '' &&
                  lineBlocks.edit !== '' &&
                  lineBlocks.split !== '' &&
                  lineBlocks.cancelLine !== '' &&
                  lineBlocks.putaway !== '' &&
                  (!lineToJitOffered || lineToJitBlock !== '') &&
                  !existingStockOffered;
                const isEditingRow = editingLineNo === line.lineNo && lineBlocks.edit === '';
                const storedLineDraft = isEditingRow && lineEditBase ? lineEditBase.draft : lineDraftOf(line);
                // Keep every refusal visible, but only values that will travel can hold the next save.
                const lineErrors: LineEditErrors = isEditingRow
                  ? editableLineErrorsOf(line, lineDraft, storedLineDraft, lineRefusals)
                  : {};
                const hasInvalidChange = (Object.keys(lineErrors) as LineEditField[]).some(
                  (field) => lineDraft[field] !== storedLineDraft[field],
                );
                const lineSaveBlock = revalidating ? RELOADING : hasInvalidChange ? FIX_LINE_FIELDS_FIRST : '';
                const purchaseOrderHref = purchaseOrderUrl(line.purchaseOrderNumber, line.purchasePlatform);
                const row = (
                  <tr key={line.lineNo}>
                    <CheckCell>
                      <Form.Check
                        type="checkbox"
                        aria-label={`Select line ${line.lineNo}`}
                        checked={activeSelection.includes(line.lineNo)}
                        onChange={() => toggleLine(line.lineNo)}
                      />
                    </CheckCell>
                    <td className="sku-cell">
                      {imageHref ? (
                        <ProductImageZoom
                          src={imageHref}
                          label={[line.brand, line.productName?.trim()].filter(Boolean).join(' ') || line.sku}
                        >
                          <img
                            src={imageHref}
                            alt={line.productName?.trim() || line.sku}
                            width={44}
                            height={44}
                            className="object-fit-cover rounded border thumb-img"
                          />
                        </ProductImageZoom>
                      ) : (
                        <span className="text-muted small no-image">No image</span>
                      )}
                      {/* The catalog link, the copy button and the Stock History link all read the trimmed sku; a
                          blank sku shows the dash and no links (SSD-7781). */}
                      {lineSku ? (
                        <span className="sku-line d-inline-flex align-items-center gap-1">
                          <a
                            className="font-monospace"
                            href={catalogEditorUrl(lineSku)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {lineSku}
                          </a>
                          <CopyIcon value={lineSku} label={`Copy SKU ${lineSku}`} />
                        </span>
                      ) : (
                        <span className="sku-line text-muted">—</span>
                      )}
                      {/* The sku still opens the catalog editor the order team edits from; this line under it opens
                          the sku's Stock History in a new tab, as the sku does on View Orders (SSD-7520). */}
                      {lineSku && (
                        <Link
                          className="sku-stock-link small d-block text-nowrap"
                          to={ROUTES.fulfillment.stockHistorySku(lineSku)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open the Stock History of ${lineSku}`}
                          title={`Open the Stock History of ${lineSku}`}
                        >
                          Stock History
                        </Link>
                      )}
                      {recalculated && line.originalSku && showOriginal(line.originalSku, line.sku) && (
                        <div className="text-muted small">({line.originalSku}*)</div>
                      )}
                    </td>
                    <td>
                      <BrandPrefixedName brand={line.brand} name={line.productName} />
                      {recalculated && (
                        <WooCommerceOriginal original={line.originalProductName} current={line.productName} />
                      )}
                    </td>
                    <td>
                      <KoreanProductName name={line.productNameKr} />
                      {recalculated && (
                        <WooCommerceOriginal original={line.originalProductNameKr} current={line.productNameKr} />
                      )}
                    </td>
                    <td>
                      {isEditingRow ? (
                        <LineEditCell
                          label={`Size for line ${line.lineNo}`}
                          value={lineDraft.size}
                          error={lineErrors.size}
                          onChange={(value) => editLineField('size', value)}
                        />
                      ) : (
                        (line.size ?? '—')
                      )}
                      {recalculated && <WooCommerceOriginal original={line.originalSize} current={line.size} />}
                    </td>
                    <td className="text-end">
                      {/* A commerce order owns its own totals: editing one here reduces a paid order [BR-25]. */}
                      {isEditingRow && !isWooCommerceOrder(header.orderId) ? (
                        <LineEditCell
                          label={`Quantity for line ${line.lineNo}`}
                          value={lineDraft.totalQuantity}
                          error={lineErrors.totalQuantity}
                          onChange={(value) => editLineField('totalQuantity', value)}
                          alignEnd
                          disabledReason={lineBlocks.quantity}
                        />
                      ) : (
                        <>
                          {line.totalQuantity}
                          {recalculated && showOriginal(line.originalQuantity, Number(line.totalQuantity)) && (
                            <span className="text-muted small"> ({line.originalQuantity}*)</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="text-end">
                      {/* The order's currency sits in the header once; the two amounts stack, subtotal over total. */}
                      {/* An amount the recalculation changed keeps its own original beside it, so a reader never pairs the
                          wrong two; an unchanged one shows once (SSD-7674). */}
                      <span className="two">
                        <span aria-label="Subtotal">
                          {(line.subtotal ?? 0).toFixed(2)}
                          {showOriginal(line.originalSubtotal, line.subtotal ?? 0) && (
                            <span className="text-muted small"> ({formatOriginalAmount(line.originalSubtotal)}*)</span>
                          )}
                        </span>
                        <small aria-label="Total">
                          {(line.total ?? 0).toFixed(2)}
                          {showOriginal(line.originalTotal, line.total ?? 0) && (
                            <span> ({formatOriginalAmount(line.originalTotal)}*)</span>
                          )}
                        </small>
                      </span>
                    </td>
                    <td className="text-end">{line.latestInventoryCount ?? stockBySku[line.sku] ?? 0}</td>
                    <td>
                      <StatusBadge status={line.lineStatus} />
                      {/* Distinct hue from the amber status badge so the flag stays scannable */}
                      {line.isSample && (
                        <Badge bg="info" className="ms-1">
                          sample
                        </Badge>
                      )}
                      <LineShelves locations={line.locations} />
                    </td>
                    <td>
                      {orderLineRouteLabel(line)}
                      {/* Where it was bought belongs with the order number and date the same purchase wrote; only a
                          server that sends the line's platform can store one. */}
                      {isEditingRow && line.purchasePlatform !== undefined && (
                        <>
                          <Form.Select
                            size="sm"
                            className="mt-1"
                            aria-label={`Purchase platform for line ${line.lineNo}`}
                            title={
                              lineDraft.purchasePlatform
                                ? (SOURCING_PLATFORM_LABELS[lineDraft.purchasePlatform as SourcingPlatform] ??
                                  lineDraft.purchasePlatform)
                                : 'No platform'
                            }
                            aria-invalid={lineErrors.purchasePlatform != null}
                            isInvalid={lineErrors.purchasePlatform != null}
                            value={lineDraft.purchasePlatform}
                            onChange={(e) => editLineField('purchasePlatform', e.target.value)}
                          >
                            <option value="">No platform</option>
                            {SOURCING_PLATFORMS.map((platform) => (
                              <option key={platform} value={platform} title={SOURCING_PLATFORM_LABELS[platform]}>
                                {EDIT_LINE_PLATFORM_LABELS[platform]}
                              </option>
                            ))}
                          </Form.Select>
                          {lineErrors.purchasePlatform && (
                            <Form.Control.Feedback type="invalid">{lineErrors.purchasePlatform}</Form.Control.Feedback>
                          )}
                        </>
                      )}
                      {/* Only a live JIT line takes a purchase decision; any other line shows a detail only if one is stored. */}
                      {(sourcingReadOnlyReason(line) === undefined || hasSourcingDetail(line)) && (
                        <div className="sourcing-detail-cell">
                          {hasSourcingDetail(line) && <div className="bot">{sourcingSummaryOf(line)}</div>}
                          <button
                            type="button"
                            className="btn btn-line btn-sm"
                            aria-label={`Sourcing detail for line ${line.lineNo}`}
                            disabled={busy}
                            title={busy ? RELOADING : ''}
                            onClick={() => setSourcingLineNo(line.lineNo)}
                          >
                            {sourcingReadOnlyReason(line) !== undefined
                              ? 'View sourcing'
                              : hasSourcingDetail(line)
                                ? 'Edit sourcing'
                                : 'Record sourcing'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="ident">
                      {isEditingRow ? (
                        <>
                          <LineEditCell
                            label={`Order number for line ${line.lineNo}`}
                            value={lineDraft.purchaseOrderNumber}
                            error={lineErrors.purchaseOrderNumber}
                            onChange={(value) => editLineField('purchaseOrderNumber', value)}
                          />
                          {/* Today wraps under the date in this one narrow column rather than widening it. */}
                          <div className="po-date-row d-flex flex-wrap align-items-start gap-1">
                            <div className="flex-grow-1">
                              <LineEditCell
                                type="date"
                                label={`Order date for line ${line.lineNo}`}
                                value={lineDraft.purchaseOrderDate}
                                error={lineErrors.purchaseOrderDate}
                                onChange={(value) => editLineField('purchaseOrderDate', value)}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn btn-line btn-sm"
                              title="Today's date (KST)"
                              disabled={busy}
                              onClick={() => editLineField('purchaseOrderDate', kstDay(new Date().toISOString()))}
                            >
                              Today
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="two">
                          {purchaseOrderHref ? (
                            <a href={purchaseOrderHref} target="_blank" rel="noreferrer">
                              {line.purchaseOrderNumber}
                            </a>
                          ) : (
                            textOf(line.purchaseOrderNumber)
                          )}
                          <small>{line.purchaseOrderDate ? textOf(line.purchaseOrderDate) : ''}</small>
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      {isEditingRow ? (
                        <LineEditCell
                          label={`Product cost for line ${line.lineNo}`}
                          value={lineDraft.productCost}
                          error={lineErrors.productCost}
                          onChange={(value) => editLineField('productCost', value)}
                          alignEnd
                        />
                      ) : (
                        agentCostText(line.productCost)
                      )}
                    </td>
                    <td className="ident">
                      {isEditingRow && (lineEditBase?.targetId || lineEditBase?.adding) ? (
                        <LineEditCell
                          label={`Tracking number for line ${line.lineNo}`}
                          value={lineDraft.lineTrackingNumber}
                          error={lineErrors.lineTrackingNumber}
                          onChange={(value) => editLineField('lineTrackingNumber', value)}
                          disabledReason={
                            !lineEditBase?.updatedAt
                              ? 'Refresh the order before editing its waybill or delivery company.'
                              : ''
                          }
                        />
                      ) : isEditingRow ? (
                        'Choose Edit or + Add waybill.'
                      ) : (
                        textOf(line.lineTrackingNumber)
                      )}
                      {isEditingRow && lineEditBase?.targetId && !lineDraft.lineTrackingNumber && (
                        <div className="text-warning small">Save row edits to delete this waybill.</div>
                      )}
                    </td>
                    <td className="delivery-company-cell">
                      <DeliveryCompanyField
                        lineNo={line.lineNo}
                        parcels={(isEditingRow ? lineEditBase?.parcels : line.lineTrackingCouriers) ?? []}
                        company={isEditingRow ? lineDraft.lineDeliveryCompany : (line.lineDeliveryCompany ?? '')}
                        editing={isEditingRow}
                        targetId={lineEditBase?.targetId}
                        disabled={line.lineTrackingCouriers === undefined || !lineEditBase?.updatedAt}
                        onCompanyChange={(value) => editLineField('lineDeliveryCompany', value)}
                        onTargetChange={selectCourierParcel}
                        onDelete={(id) => selectCourierParcel(id, true)}
                        onAdd={() => selectCourierParcel('')}
                        adding={lineEditBase?.adding}
                      />
                      {isEditingRow && line.lineTrackingCouriers === undefined && (
                        <div className="small text-muted">
                          Delivery company editing requires the updated detail API.
                        </div>
                      )}
                      {isEditingRow && line.lineTrackingCouriers !== undefined && !lineEditBase?.updatedAt && (
                        <div className="small text-muted">
                          Refresh the order before editing its waybill or delivery company.
                        </div>
                      )}
                      {isEditingRow && lineErrors.lineDeliveryCompany && (
                        <div className="text-danger small">{lineErrors.lineDeliveryCompany}</div>
                      )}
                    </td>
                    <td className="cp-cell">
                      {/* Read-only even in Edit line: the CP link belongs to SS Editor, and a link an operator wants to
                          keep goes in this line's Memo (SSD-7588). */}
                      {purchaseHref ? (
                        // The address itself, cut short with an ellipsis where the column ends; the whole url is the link
                        // and its tooltip (SSD-7441). It named only the site before, which hid which product it was.
                        <a className="ff-url" href={purchaseHref} target="_blank" rel="noreferrer" title={purchaseHref}>
                          {shortUrl(purchaseHref)}
                        </a>
                      ) : (
                        '—'
                      )}
                      {isEditingRow && <span className="cp-hint">Read-only · put other links in Memo</span>}
                    </td>
                    <td className="memo-cell">
                      {/* The line's own memos, whole, and the box that writes the next one; View Orders reads them under the line (SSD-7265). */}
                      {memos.length > 0 && (
                        <ul className="memo-list" aria-label={`Line ${line.lineNo} memos`}>
                          {memos.map((memo, index) => (
                            <li key={`${index}-${memo.at}`}>
                              <span className="memo-body">
                                <span className="memo-text">
                                  <LinkedText text={memo.text} />
                                </span>
                                <span className="memo-meta">
                                  {memo.author} · <time>{kstTimestamp(memo.at)}</time>
                                </span>
                              </span>
                              <button
                                type="button"
                                className="memo-x"
                                aria-label={`Delete memo on line ${line.lineNo}: ${memo.text}`}
                                title="Delete this memo"
                                disabled={busy}
                                onClick={() => void deleteComment(memo, line.lineNo)}
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <input
                        className="memo-input"
                        aria-label={`Memo for line ${line.lineNo}`}
                        // The whole hint did not fit the box ("Memo, Enter pos"); the sentence is the tooltip (SSD-7674).
                        placeholder="Memo + Enter"
                        title="Type a memo and press Enter to post it"
                        value={memoDrafts[line.lineNo] ?? ''}
                        readOnly={busy}
                        onChange={(event) => setMemoDraft(line.lineNo, event.target.value)}
                        onKeyDown={(event) => {
                          // The Enter that ends Korean composition is not a post.
                          if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                            event.preventDefault();
                            void addLineMemo(line.lineNo);
                          }
                        }}
                      />
                    </td>
                    <td>
                      {/* ✓/✕ came out as bare glyphs: this sheet paints no border on bootstrap outlines. */}
                      {isEditingRow ? (
                        <div className="d-flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="btn btn-green-line btn-sm"
                            aria-label="Save row edits"
                            disabled={busy || lineSaveBlock !== ''}
                            title={lineSaveBlock}
                            onClick={() => void saveRowEdits(line)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-red-line btn-sm"
                            aria-label="Discard row edits"
                            onClick={closeRowEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex flex-column align-items-start gap-1 row-actions">
                          <button
                            key="edit-line"
                            type="button"
                            className="btn btn-line btn-sm"
                            disabled={busy || lineBlocks.edit !== ''}
                            title={lineBlocks.edit}
                            onClick={() => openRowEdit(line)}
                          >
                            <Pencil size={14} aria-hidden="true" /> Edit
                          </button>
                          {/* Inbound stays a button: it is the one the floor presses all day. */}
                          <button
                            type="button"
                            className="btn btn-line btn-sm"
                            disabled={busy || lineBlocks.inbound !== ''}
                            title={lineBlocks.inbound}
                            onClick={() => void inboundLine(line.lineNo)}
                          >
                            Inbound
                          </button>
                          {/*
                          The other five wrapped onto five lines in a 190px cell and drove every row to
                          260px, so they fold into a menu. Each refusal now sits on its own item instead
                          of stacking as loose text under the cell, where it named no button.
                        */}
                          <Dropdown>
                            {/*
                            A bare ⋯ read as a grey blob beside Inbound: the sheet strips bootstrap's
                            outline border and fills the toggle instead. Same idiom as every other
                            control on this page, and the word says a menu opens.
                          */}
                            <Dropdown.Toggle
                              as="button"
                              type="button"
                              className="btn btn-line btn-sm line-more"
                              aria-label="More line actions"
                              disabled={busy || everyFoldedActionBlocked}
                              title={
                                busy ? RELOADING : everyFoldedActionBlocked ? 'Nothing can be done to this line' : ''
                              }
                            >
                              More ▾
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="line-menu" popperConfig={ROW_MENU_POPPER}>
                              <Dropdown.Item
                                as="button"
                                type="button"
                                disabled={busy || lineBlocks.cancelInbound !== ''}
                                title={lineBlocks.cancelInbound}
                                onClick={() => cancelInboundLine(line.lineNo)}
                              >
                                Cancel Inbound
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                type="button"
                                disabled={busy || lineBlocks.edit !== ''}
                                title={lineBlocks.edit}
                                onClick={() => openRowEdit(line)}
                              >
                                Edit line
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                type="button"
                                disabled={busy || lineBlocks.split !== ''}
                                title={lineBlocks.split}
                                onClick={() => openSplitLine(line.lineNo)}
                              >
                                Split line
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                type="button"
                                disabled={busy || lineBlocks.putaway !== ''}
                                title={lineBlocks.putaway}
                                onClick={() => void returnPutaway(line.lineNo)}
                              >
                                Return putaway
                              </Dropdown.Item>
                              {lineToJitOffered && (
                                <Dropdown.Item
                                  as="button"
                                  type="button"
                                  disabled={busy || lineToJitBlock !== ''}
                                  title={lineToJitBlock}
                                  onClick={() => openLineToJit(line.lineNo)}
                                >
                                  No stock → awaiting purchase
                                </Dropdown.Item>
                              )}
                              {existingStockOffered && (
                                <Dropdown.Item
                                  as="button"
                                  type="button"
                                  disabled={busy}
                                  onClick={() => {
                                    setExistingStockRefusal(undefined);
                                    setExistingStockTarget({ orderId: header.orderId, line });
                                  }}
                                >
                                  Use existing stock
                                </Dropdown.Item>
                              )}
                              {/* Below the line: the action that takes the line away. */}
                              <Dropdown.Divider />
                              <Dropdown.Item
                                as="button"
                                type="button"
                                className="text-danger"
                                disabled={busy || lineBlocks.cancelLine !== ''}
                                title={lineBlocks.cancelLine}
                                onClick={() => void cancelLine(line.lineNo)}
                              >
                                Cancel line
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          {/* The menu is shut while the order reloads, so the row says the reason where it can be read. */}
                          {busy && <span className="text-muted small">{RELOADING}</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
                return row;
              })}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={LINE_ITEM_COLUMNS} className="text-center text-muted py-4">
                    No lines on this order.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* .li-foot — the wireframe puts Outbound here, beside Bulk Inbound, under the lines it judges. */}
        <div className="li-foot">
          <span className="tq">Total Quantity: {liveLines.reduce((sum, line) => sum + line.totalQuantity, 0)}</span>
          <button
            type="button"
            className="btn btn-green-line btn-sm"
            disabled={bulkInboundBlock !== ''}
            title={bulkInboundBlock}
            onClick={() => void bulkInboundSelected()}
          >
            Bulk Inbound Selected Items
          </button>
          {header.fulfillmentStatus === 'OUTBOUNDED' ? (
            <button
              type="button"
              className="btn btn-red-line btn-sm"
              disabled={busy}
              onClick={() => void cancelOutboundOrder()}
            >
              Cancel Outbound
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-green btn-sm"
              disabled={busy || !canOutbound}
              title={outboundBlock}
              onClick={() => void outboundOrder()}
            >
              📦 Outbound
            </button>
          )}
          {/* Adding a line is a line action, so its button sits with the other line-table actions. */}
          <button type="button" className="btn btn-line btn-sm" disabled={busy} onClick={() => setAddingLine(true)}>
            + Add Line Item
          </button>
          {/* One grey string joining two buttons' reasons named neither, so each reason names its own. */}
          {(bulkInboundBlock !== '' || outboundBlock !== '') && (
            <div className="li-foot-blocks">
              {bulkInboundBlock !== '' && <span className="blocked-note">Bulk Inbound — {bulkInboundBlock}</span>}
              {outboundBlock !== '' && <span className="blocked-note">Outbound — {outboundBlock}</span>}
            </div>
          )}
        </div>
      </div>

      {/*
        Outbound reads the whole order, not one line, so it sits under the lines it is judging —
        and the reason it is refused sits with it rather than in the header bar [wireframe 2, 9].
      */}

      <AddLineItemModal show={addingLine} busy={busy} onHide={() => setAddingLine(false)} onSubmit={addLine} />
      <CloneOrderModal
        show={cloning}
        orderId={header.orderId}
        lines={lines}
        reservations={reservations}
        currency={orderCurrency}
        onHide={() => setCloning(false)}
        onSubmit={submitClone}
      />
      <CreateCarrierOrderModal
        show={bookingCarrier}
        orderId={header.orderId}
        madeFor={detail.relations?.cloneOf?.provider ?? null}
        onHide={() => setBookingCarrier(false)}
        onConfirm={(provider) => void bookCarrierOrder(provider)}
      />
      <SwitchCarrierOrderModal
        show={switching !== null}
        orderId={header.orderId}
        source={switching?.source ?? null}
        onHide={() => setSwitching(null)}
        onSubmit={submitSwitch}
      />

      {/* .logsec — the wireframe's own last section, and it labels this one "New". */}
      <div className="logsec">
        <h4>
          Inbound / Outbound Actor Log <span className="blocked-note">— New</span>
        </h4>
        <table className="logtbl" aria-label="Inbound / Outbound Actor Log">
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
              <th>SKU</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th>Operator</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {actorLogRows.map((row) => (
              <tr key={row.id}>
                <td className="num">{row.at}</td>
                <td className={row.actionClass}>{row.action}</td>
                <td className="num">{row.sku}</td>
                <td className="num" style={{ textAlign: 'right' }}>
                  {row.qty}
                </td>
                <td>{row.operator}</td>
                <td>{row.note}</td>
              </tr>
            ))}
            {actorLogRows.length === 0 && (
              <tr>
                <td colSpan={6} className="blocked-note" style={{ textAlign: 'center', padding: '18px 0' }}>
                  No inbound or outbound action recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/*
        Not in the wireframe: a cost or purchase field someone typed wrong is traced here to the person and the moment.
        The events keep only the value each save set, so the earlier value is said to be unrecorded rather than guessed.
      */}
      <details className="logsec editlog" data-testid="edit-log">
        <summary>
          Edit Log — who changed what{' '}
          <span className="blocked-note">
            ({editRows.length} {editRows.length === 1 ? 'change' : 'changes'})
          </span>
        </summary>
        <table className="logtbl editlog-tbl" aria-label="Edit Log">
          <colgroup>
            <col className="c-time" />
            <col className="c-who" />
            <col className="c-line" />
            <col className="c-field" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>Time</th>
              <th>Who</th>
              <th>Line</th>
              <th>Field</th>
              <th>Entered value</th>
            </tr>
          </thead>
          <tbody>
            {editRows.map((row) => (
              <tr key={row.id}>
                <td className="num">{row.at}</td>
                <td>{row.actor}</td>
                <td className="num">{row.line}</td>
                <td>{row.field}</td>
                <td>
                  <span className="editlog-value">{row.value}</span>
                  <span className="editlog-prev">
                    {row.previous === PREVIOUS_NOT_RECORDED ? `(${PREVIOUS_NOT_RECORDED})` : `(was ${row.previous})`}
                  </span>
                </td>
              </tr>
            ))}
            {editRows.length === 0 && (
              <tr>
                <td colSpan={5} className="blocked-note" style={{ textAlign: 'center', padding: '18px 0' }}>
                  No line edit recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </details>

      {/*
        Neither block is in the wireframe: it was drawn for the warehouse floor and these two are ours,
        for reading what the screens above claim. Kept, and folded, so the floor scrolls past them.
      */}
      <details className="debug-reads" data-testid="debug-reads">
        <summary>Debug reads — reservations and the event ledger</summary>
        <div className="sec">
          <h3>🔒 Reservations ({reservations.length})</h3>
          <table className="tbl">
            <thead>
              <tr>
                <th>Line</th>
                <th>SKU</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Kind</th>
                <th>State</th>
                <th>Location</th>
                <th>Released reason</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((reservation, index) => (
                <tr key={`${reservation.lineNo}-${reservation.sku}-${index}`}>
                  <td>{reservation.lineNo}</td>
                  <td className="num">{reservation.sku}</td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {reservation.qty}
                  </td>
                  <td>{reservation.kind}</td>
                  <td>
                    <StatusBadge status={reservation.state} />
                  </td>
                  <td className="num">{reservation.locationCode ?? '—'}</td>
                  <td>{reservation.releasedReason ?? '—'}</td>
                </tr>
              ))}
              {reservations.length === 0 && (
                <tr>
                  <td colSpan={7} className="blocked-note" style={{ textAlign: 'center', padding: '18px 0' }}>
                    No stock is reserved for this order.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="sec">
          <h3>🧭 Events ({events.length})</h3>
          <div className="trackscroll">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Kind</th>
                  <th>Operation</th>
                  <th>Actor</th>
                  <th>Source</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event) => (
                  <tr key={event.id}>
                    <td className="num">{kstTimestamp(event.occurredAt)}</td>
                    <td>
                      <span className="node">{event.kind}</span>
                    </td>
                    <td>{describeOperation(event.operation)}</td>
                    <td>{event.actor ?? '—'}</td>
                    <td>{event.source}</td>
                    <td>
                      <details>
                        <summary className="blocked-note">Payload</summary>
                        <pre className="num" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(event.payload, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={6} className="blocked-note" style={{ textAlign: 'center', padding: '18px 0' }}>
                      No events recorded for this order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </details>
      {splitTarget?.orderId === header.orderId &&
        lines.some((candidate) => candidate.lineNo === splitTarget.lineNo) && (
          <SplitLineModal
            line={lines.find((candidate) => candidate.lineNo === splitTarget.lineNo)!}
            currency={orderCurrency}
            busy={busy}
            refusal={splitRefusal}
            onHide={closeSplitLine}
            onConfirm={(parts) => void confirmSplitLine(parts)}
          />
        )}
      {toJitTarget?.orderId === header.orderId &&
        lines.some((candidate) => candidate.lineNo === toJitTarget.lineNo) && (
          <SwitchLineToJitModal
            line={lines.find((candidate) => candidate.lineNo === toJitTarget.lineNo)!}
            busy={busy}
            refusal={toJitRefusal}
            onHide={closeLineToJit}
            onConfirm={(ask) => void confirmLineToJit(ask)}
          />
        )}
      {existingStockTarget?.orderId === header.orderId && (
        <UseExistingStockModal
          client={client}
          line={existingStockTarget.line}
          busy={busy}
          changed={
            !lines.some(
              (line) =>
                line.lineNo === existingStockTarget.line.lineNo &&
                line.updatedAt === existingStockTarget.line.updatedAt &&
                offersLineToExistingInventory(line, detail),
            )
          }
          refusal={existingStockRefusal}
          onHide={() => setExistingStockTarget(null)}
          onConfirm={(ask) => void confirmExistingStock(ask)}
        />
      )}
      {cancelInboundTarget && (
        <CancelInboundLineModal
          sku={cancelInboundTarget.sku}
          lineNo={cancelInboundTarget.lineNo}
          arrivedQty={cancelInboundTarget.arrivedQty}
          busy={busy}
          onHide={() => setCancelInboundTarget(null)}
          onConfirm={confirmCancelInbound}
        />
      )}
      {sourcingLine && (
        <LineSourcingDetailModal
          key={sourcingLine.lineNo}
          line={sourcingLine}
          readOnlyReason={sourcingReadOnlyReason(sourcingLine)}
          onHide={() => setSourcingLineNo(null)}
          onSave={(request) => client.updateLineSourcingDetail(header.orderId, sourcingLine.lineNo, request)}
          onSaved={async () => {
            setSourcingLineNo(null);
            showSuccess(`Line ${sourcingLine.lineNo} sourcing detail saved.`);
            await refreshDetail();
          }}
          onConflict={refreshDetail}
        />
      )}
      {promptDialog}
      {receivedGoodsDialog}
      {cogsReportOpen && (
        <CogsReportDialog
          client={client}
          orderId={header.orderId}
          currency={orderCurrency}
          lines={lines}
          onClose={() => setCogsReportOpen(false)}
          onSent={showSuccess}
        />
      )}
      {catalogRequestOpen && (
        <CatalogRequestDialog
          client={client}
          orderId={header.orderId}
          lines={lines}
          // One checked line is what the operator was looking at; several or none leaves the choice to them.
          initialLineNo={activeSelection.length === 1 ? activeSelection[0]! : null}
          onClose={() => setCatalogRequestOpen(false)}
          onSent={showSuccess}
        />
      )}
    </div>
  );
}
